import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { PointsApiHelpPage } from './PointsApiHelp';

type TabType = 'dashboard' | 'users' | 'earning' | 'expiry' | 'catalog' | 'redemption' | 'orders' | 'migration' | 'notifications' | 'payment' | 'bbps' | 'audit' | 'api-help';

export function PointsManagement() {
  const [activeTab, setActiveTab] = useState<TabType>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  
  // Dashboard state
  const [dashboard, setDashboard] = useState<any>(null);
  const [activity, setActivity] = useState<any>(null);
  
  // Users state
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userNameMap, setUserNameMap] = useState<Map<string, string>>(new Map());
  
  // Config states
  const [earningConfig, setEarningConfig] = useState<any>(null);
  const [expiryConfig, setExpiryConfig] = useState<any>(null);
  const [redemptionConfig, setRedemptionConfig] = useState<any>(null);
  const [paymentConfig, setPaymentConfig] = useState<any>(null);
  const [migrationConfig, setMigrationConfig] = useState<any>(null);
  const [notificationConfig, setNotificationConfig] = useState<any>(null);
  
  // Expiry state
  const [expiryStats, setExpiryStats] = useState<any>(null);
  
  // Catalog state
  const [categories, setCategories] = useState<any[]>([]);
  const [operators, setOperators] = useState<any[]>([]);
  const [catalogTipDismissed, setCatalogTipDismissed] = useState(false);
  
  // Orders state
  const [orders, setOrders] = useState<any[]>([]);
  
  // Migration state
  const [migrationStats, setMigrationStats] = useState<any>(null);
  const [migrationBatches, setMigrationBatches] = useState<any[]>([]);
  
  // Audit state
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditActions, setAuditActions] = useState<any>(null);

  // BBPS Gateway state
  const [bbpsConfig, setBbpsConfig] = useState<any>(null);
  const [bbpsProviders, setBbpsProviders] = useState<any[]>([]);
  const [bbpsSyncStatus, setBbpsSyncStatus] = useState<any>(null);
  const [bbpsHealth, setBbpsHealth] = useState<any>(null);
  const [bbpsSyncing, setBbpsSyncing] = useState(false);
  const [bbpsEditModal, setBbpsEditModal] = useState<'payu' | 'razorpay' | null>(null);
  const [bbpsEditData, setBbpsEditData] = useState<any>(null);

  // Modals
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustUserId, setAdjustUserId] = useState('');
  const [adjustPoints, setAdjustPoints] = useState(0);
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustType, setAdjustType] = useState<'credit' | 'debit'>('credit');

  // Edit modals
  const [editModal, setEditModal] = useState<string | null>(null);
  const [editData, setEditData] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  // New Migration Batch state
  const [showNewBatchModal, setShowNewBatchModal] = useState(false);
  const [newBatchName, setNewBatchName] = useState('');
  const [newBatchUserIds, setNewBatchUserIds] = useState('');
  const [newBatchDescription, setNewBatchDescription] = useState('');

  // Batch Details state
  const [selectedBatch, setSelectedBatch] = useState<any>(null);
  const [batchDetailsLoading, setBatchDetailsLoading] = useState(false);

  // Sidebar collapsed state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Migration sub-sidebar state
  const [migrationSubView, setMigrationSubView] = useState<'batches' | 'sql-users'>('batches');
  const [migrationSubSidebarCollapsed, setMigrationSubSidebarCollapsed] = useState(false);
  const [sqlUsers, setSqlUsers] = useState<any[]>([]);
  const [sqlUsersLoading, setSqlUsersLoading] = useState(false);
  const [selectedSqlUsers, setSelectedSqlUsers] = useState<Set<string>>(new Set());
  const [sqlUsersFetchLimit, setSqlUsersFetchLimit] = useState<number | 'all' | 'custom'>(50);
  const [sqlUsersCustomLimit, setSqlUsersCustomLimit] = useState<string>('');
  const [sqlUsersSearchTerm, setSqlUsersSearchTerm] = useState<string>('');
  const [pendingMigrationUsers, setPendingMigrationUsers] = useState<Array<{ userId: string; legacyPoints: number; name: string }>>([]);
  const [migratedUsersMap, setMigratedUsersMap] = useState<Map<string, { balance: number; migratedAt?: string }>>(new Map());
  const [showMigratedUsers, setShowMigratedUsers] = useState<boolean>(true);

  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const loadTabData = async (tab: TabType) => {
    setLoading(true);
    setError(null);
    
    try {
      switch (tab) {
        case 'dashboard':
          const [dashRes, actRes] = await Promise.all([
            api.getPointsDashboard(),
            api.getPointsActivity('today'),
          ]);
          setDashboard((dashRes as any).data);
          setActivity((actRes as any).data);
          break;
          
        case 'users':
          // Load all Points users and fetch names from SQL
          const [usersRes, sqlNamesRes] = await Promise.all([
            api.searchPointsUsers(userSearch || '', 100),
            api.getLeaderboard({ top: 10000 }) // Get all SQL users for name lookup
          ]);
          const pointsUsers = (usersRes as any).data?.users || [];
          const sqlUsersList = (sqlNamesRes as any).users || [];
          
          // Build name lookup map from SQL users
          const nameMap = new Map<string, string>();
          for (const su of sqlUsersList) {
            nameMap.set(su.userId?.toString(), su.name);
          }
          setUserNameMap(nameMap);
          setUsers(pointsUsers);
          break;
          
        case 'earning':
          const earnRes = await api.getPointsConfig('earning');
          setEarningConfig((earnRes as any).data);
          break;
          
        case 'expiry':
          const [expCfgRes, expStatsRes] = await Promise.all([
            api.getPointsConfig('expiry'),
            api.getExpiryStats(),
          ]);
          setExpiryConfig((expCfgRes as any).data);
          setExpiryStats((expStatsRes as any).data);
          break;

        case 'redemption':
          const redCfgRes = await api.getPointsConfig('redemption');
          setRedemptionConfig((redCfgRes as any).data);
          break;
          
        case 'catalog':
          const [catRes, opRes] = await Promise.all([
            api.getPointsCategories(),
            api.getPointsOperators(),
          ]);
          setCategories((catRes as any).data?.categories || []);
          setOperators((opRes as any).data?.operators || []);
          break;
          
        case 'orders':
          const ordersRes = await api.getRedemptionOrders({ limit: 50 });
          setOrders((ordersRes as any).data?.orders || []);
          break;
          
        case 'migration':
          const [migStatsRes, migBatchRes, migCfgRes] = await Promise.all([
            api.getMigrationStats(),
            api.getMigrationBatches(),
            api.getPointsConfig('migration'),
          ]);
          setMigrationStats((migStatsRes as any).data);
          setMigrationBatches((migBatchRes as any).data?.batches || []);
          setMigrationConfig((migCfgRes as any).data);
          break;
          
        case 'notifications':
          const notifCfgRes = await api.getPointsConfig('notifications');
          setNotificationConfig((notifCfgRes as any).data);
          break;
          
        case 'payment':
          const payCfgRes = await api.getPointsConfig('payment_gateway');
          setPaymentConfig((payCfgRes as any).data);
          break;
          
        case 'bbps':
          const [bbpsConfigRes, bbpsProvidersRes, bbpsSyncStatusRes] = await Promise.all([
            api.getBBPSConfig(),
            api.getBBPSProviders(),
            api.getBBPSSyncStatus(),
          ]);
          setBbpsConfig((bbpsConfigRes as any).config || null);
          setBbpsProviders((bbpsProvidersRes as any).providers || []);
          setBbpsSyncStatus((bbpsSyncStatusRes as any) || null);
          // Try to get health status (may fail if factory not initialized)
          try {
            const healthRes = await api.getBBPSHealth();
            setBbpsHealth((healthRes as any).health || null);
          } catch {
            setBbpsHealth(null);
          }
          break;
          
        case 'audit':
          const [auditRes, actionsRes] = await Promise.all([
            api.getPointsAuditLogs({ limit: 30 }),
            api.getPointsActivity('week'),
          ]);
          setAuditLogs((auditRes as any).data?.logs || []);
          setAuditActions((actionsRes as any).data);
          break;
          
        case 'api-help':
          // No data to load - static help page
          break;
      }
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : JSON.stringify(err));
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchUsers = async (searchTerm?: string) => {
    setLoading(true);
    try {
      const term = (searchTerm !== undefined ? searchTerm : userSearch).toLowerCase().trim();
      
      // Fetch all users and names
      const [usersRes, sqlNamesRes] = await Promise.all([
        api.searchPointsUsers('', 100),
        api.getLeaderboard({ top: 10000 })
      ]);
      
      const allPointsUsers = (usersRes as any).data?.users || [];
      const sqlUsersList = (sqlNamesRes as any).users || [];
      
      // Build name lookup map
      const nameMap = new Map<string, string>();
      for (const su of sqlUsersList) {
        nameMap.set(su.userId?.toString(), su.name);
      }
      setUserNameMap(nameMap);
      
      // Filter by search term (matches userId OR name)
      if (term) {
        const filtered = allPointsUsers.filter((u: any) => {
          const userId = u.userId?.toString().toLowerCase() || '';
          const userName = nameMap.get(u.userId?.toString())?.toLowerCase() || '';
          return userId.includes(term) || userName.includes(term);
        });
        setUsers(filtered);
      } else {
        setUsers(allPointsUsers);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = async (userId: string) => {
    try {
      const res = await api.getPointsUserDetails(userId);
      setSelectedUser((res as any).data);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAdjustPoints = async () => {
    try {
      await api.adjustUserPoints(adjustUserId, {
        points: adjustPoints,
        reason: adjustReason,
        type: adjustType,
      });
      setShowAdjustModal(false);
      setAdjustUserId('');
      setAdjustPoints(0);
      setAdjustReason('');
      setSuccessMsg('Points adjusted successfully!');
      loadTabData('users');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRunExpiryJob = async () => {
    try {
      setLoading(true);
      await api.runExpiryJob();
      setSuccessMsg('Expiry job completed!');
      loadTabData('expiry');
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCompleteOrder = async (orderId: string) => {
    try {
      await api.completeRedemptionOrder(orderId);
      setSuccessMsg('Order completed!');
      loadTabData('orders');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openEditModal = (type: string, data: any) => {
    setEditData(JSON.parse(JSON.stringify(data))); // Deep clone
    setEditModal(type);
  };

  const handleSaveConfig = async (configType: string, data: any) => {
    setSaving(true);
    try {
      await api.updatePointsConfig(configType, data);
      setSuccessMsg(`${configType} configuration saved!`);
      setEditModal(null);
      loadTabData(activeTab);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleExecuteBatch = async (batchId: string, usersToMigrate?: Array<{ userId: string; legacyPoints: number }>) => {
    try {
      setLoading(true);
      // If users not provided, we need to get them from the batch or SQL
      const users = usersToMigrate || [];
      if (users.length === 0) {
        // Try to get batch details to find users
        const batchDetails = await api.getMigrationBatchDetails(batchId);
        const batchData = (batchDetails as any).data;
        if (batchData?.users && batchData.users.length > 0) {
          users.push(...batchData.users.map((u: any) => ({
            userId: u.userId,
            legacyPoints: u.legacyPoints || u.totalRewardAmount || 0
          })));
        }
      }
      
      if (users.length === 0) {
        setError('No users found in batch to migrate. Please add users with their legacy points.');
        setLoading(false);
        return;
      }
      
      await api.executeMigrationBatch(batchId, users);
      setSuccessMsg('Migration batch executed successfully!');
      loadTabData('migration');
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : JSON.stringify(err));
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleRollbackBatch = async (batchId: string) => {
    if (!confirm('Are you sure you want to rollback this migration batch?')) return;
    try {
      setLoading(true);
      await api.rollbackMigrationBatch(batchId);
      setSuccessMsg('Migration batch rolled back! User migration status will refresh.');
      // Clear the migrated users cache so it refreshes on next view
      setMigratedUsersMap(new Map());
      // Refresh both migration data and SQL users
      loadTabData('migration');
      // Also refresh SQL users to update migration status
      if (sqlUsers.length > 0) {
        fetchSqlUsers();
      }
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : JSON.stringify(err));
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleViewBatchDetails = async (batch: any) => {
    setBatchDetailsLoading(true);
    setSelectedBatch(batch); // Show modal with basic info immediately
    try {
      const res = await api.getMigrationBatchDetails(batch.id);
      setSelectedBatch((res as any).data);
    } catch (err: any) {
      setError(`Failed to load batch details: ${err.message}`);
    } finally {
      setBatchDetailsLoading(false);
    }
  };

  // Fetch SQL users from leaderboard API (uses existing Content Management API)
  const fetchSqlUsers = async () => {
    setSqlUsersLoading(true);
    try {
      // Determine the limit to use
      let topValue: number;
      if (sqlUsersFetchLimit === 'all') {
        topValue = 10000; // Large number to fetch all users
      } else if (sqlUsersFetchLimit === 'custom') {
        const customNum = parseInt(sqlUsersCustomLimit, 10);
        if (isNaN(customNum) || customNum < 1) {
          setError('Please enter a valid number for custom limit');
          setSqlUsersLoading(false);
          return;
        }
        topValue = customNum;
      } else {
        topValue = sqlUsersFetchLimit;
      }
      
      // Fetch SQL users first
      const sqlRes = await api.getLeaderboard({ top: topValue });
      const sqlUsersList = (sqlRes as any).users || [];
      setSqlUsers(sqlUsersList);
      
      // Try to fetch Points users to check migration status (optional - don't fail if this errors)
      try {
        const pointsRes = await api.searchPointsUsers('', 10000);
        const pointsUsersList = (pointsRes as any).data?.users || (pointsRes as any).users || [];
        
        // Build a map of migrated users (only users with isMigrated = true)
        const migratedMap = new Map<string, { balance: number; migratedAt?: string }>();
        for (const pu of pointsUsersList) {
          // Only consider user as migrated if isMigrated flag is true
          if (pu.isMigrated === true) {
            migratedMap.set(pu.userId?.toString(), {
              balance: pu.availablePoints || pu.totalPoints || 0,
              migratedAt: pu.lastActivityAt
            });
          }
        }
        setMigratedUsersMap(migratedMap);
      } catch (pointsErr) {
        // Points API failed - just continue without migration status
        console.warn('Could not fetch Points users for migration status:', pointsErr);
        setMigratedUsersMap(new Map());
      }
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : 'Unknown error fetching SQL users');
      setError(`Failed to fetch SQL users: ${errorMsg}`);
    } finally {
      setSqlUsersLoading(false);
    }
  };

  // Toggle SQL user selection
  const toggleSqlUserSelection = (userId: string) => {
    setSelectedSqlUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Select all SQL users
  // Select all SQL users (respects current search filter and excludes migrated users)
  const selectAllSqlUsers = () => {
    const filteredUsers = sqlUsers
      .filter(u => showMigratedUsers || !migratedUsersMap.has(u.userId?.toString()))
      .filter(u => !sqlUsersSearchTerm || u.name?.toLowerCase().includes(sqlUsersSearchTerm.toLowerCase()))
      .filter(u => !migratedUsersMap.has(u.userId?.toString())); // Only select non-migrated users
    
    const filteredUserIds = filteredUsers.map(u => u.userId);
    
    // Check if all filtered users are already selected
    const allFilteredSelected = filteredUserIds.length > 0 && filteredUserIds.every(id => selectedSqlUsers.has(id));
    
    if (allFilteredSelected) {
      // Deselect all filtered users
      setSelectedSqlUsers(prev => {
        const newSet = new Set(prev);
        filteredUserIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Select all filtered users (add to existing selection)
      setSelectedSqlUsers(prev => {
        const newSet = new Set(prev);
        filteredUserIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  // Add selected SQL users to new batch modal
  const addSelectedUsersToNewBatch = () => {
    const selectedUserData = sqlUsers
      .filter(u => selectedSqlUsers.has(u.userId))
      .map(u => ({
        userId: u.userId,
        legacyPoints: u.totalRewardAmount || 0,
        name: u.name
      }));
    
    const userIds = selectedUserData.map(u => u.userId).join('\n');
    setNewBatchUserIds(prev => prev ? `${prev}\n${userIds}` : userIds);
    setPendingMigrationUsers(selectedUserData);
    setNewBatchName(`Bulk migration - ${selectedUserData.length} users`);
    setShowNewBatchModal(true);
    setSelectedSqlUsers(new Set());
  };

  const handleCreateBatch = async () => {
    if (!newBatchName.trim()) {
      setError('Batch name is required');
      return;
    }
    setSaving(true);
    try {
      await api.createMigrationBatch({
        name: newBatchName,
        description: newBatchDescription,
      });
      setSuccessMsg('Migration batch created! You can now execute it from the Batches list.');
      setShowNewBatchModal(false);
      setNewBatchName('');
      setNewBatchUserIds('');
      setNewBatchDescription('');
      setPendingMigrationUsers([]);
      loadTabData('migration');
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : JSON.stringify(err));
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Create batch and immediately execute with pending users
  const handleCreateAndExecuteBatch = async () => {
    if (!newBatchName.trim()) {
      setError('Batch name is required');
      return;
    }
    if (pendingMigrationUsers.length === 0) {
      setError('No users selected for migration');
      return;
    }
    setSaving(true);
    try {
      // Step 1: Create the batch
      const createRes = await api.createMigrationBatch({
        name: newBatchName,
        description: newBatchDescription,
      });
      
      const batchId = (createRes as any).data?.id;
      if (!batchId) {
        throw new Error('Failed to create batch - no batch ID returned');
      }

      // Step 2: Execute with the pending users
      const usersToMigrate = pendingMigrationUsers.map(u => ({
        userId: u.userId,
        legacyPoints: u.legacyPoints
      }));

      await api.executeMigrationBatch(batchId, usersToMigrate);
      
      const totalPoints = pendingMigrationUsers.reduce((s, u) => s + u.legacyPoints, 0);
      const convertedPoints = totalPoints * (migrationConfig?.settings?.conversionRate || 100);
      
      setSuccessMsg(`Migration completed! ${pendingMigrationUsers.length} user(s) migrated with ${formatNumber(convertedPoints)} points.`);
      setShowNewBatchModal(false);
      setNewBatchName('');
      setNewBatchUserIds('');
      setNewBatchDescription('');
      setPendingMigrationUsers([]);
      // Clear the migrated users cache so it refreshes
      setMigratedUsersMap(new Map());
      loadTabData('migration');
      // Also refresh SQL users to update migration status
      if (sqlUsers.length > 0) {
        fetchSqlUsers();
      }
    } catch (err: any) {
      const errorMsg = err?.message || err?.error || (typeof err === 'string' ? err : JSON.stringify(err));
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'dashboard' as TabType, label: 'Dashboard', icon: '📊' },
    { id: 'users' as TabType, label: 'Users', icon: '👥' },
    { id: 'earning' as TabType, label: 'Earning', icon: '💰' },
    { id: 'expiry' as TabType, label: 'Expiry', icon: '⏰' },
    { id: 'redemption' as TabType, label: 'Redemption', icon: '🎁' },
    { id: 'catalog' as TabType, label: 'Catalog', icon: '📦' },
    { id: 'orders' as TabType, label: 'Orders', icon: '🛒' },
    { id: 'migration' as TabType, label: 'Migration', icon: '🔄' },
    { id: 'notifications' as TabType, label: 'Notifications', icon: '🔔' },
    { id: 'payment' as TabType, label: 'Payment', icon: '💳' },
    { id: 'bbps' as TabType, label: 'BBPS Gateway', icon: '🔌' },
    { id: 'audit' as TabType, label: 'Audit', icon: '📋' },
    { id: 'api-help' as TabType, label: 'API Help', icon: '📖' },
  ];

  const formatNumber = (num: number) => new Intl.NumberFormat('en-IN').format(num || 0);
  const formatDate = (dateStr: string) => dateStr ? new Date(dateStr).toLocaleString('en-IN') : '-';

  return (
    <div className="flex h-[calc(100vh-80px)]">
      {/* Left Sidebar Navigation - Collapsible */}
      <div className={`${sidebarCollapsed ? 'w-14' : 'w-56'} bg-white border-r border-gray-200 overflow-y-auto flex-shrink-0 transition-all duration-200 relative`}>
        {/* Collapse/Expand Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors z-10"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? '»' : '«'}
        </button>
        
        <nav className={`${sidebarCollapsed ? 'pt-10' : 'pt-8'} pb-2`}>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              title={sidebarCollapsed ? tab.label : undefined}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-2.5 text-sm font-medium transition-colors text-left border-l-2 ${
                activeTab === tab.id
                  ? 'border-linepe-orange bg-orange-50 text-linepe-orange'
                  : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span className={sidebarCollapsed ? 'text-xl' : 'text-lg'}>{tab.icon}</span>
              {!sidebarCollapsed && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6">
          {/* Alerts */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex justify-between">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="font-bold">×</button>
            </div>
          )}
          {successMsg && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex justify-between">
              <span>{successMsg}</span>
              <button onClick={() => setSuccessMsg(null)} className="font-bold">×</button>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center min-h-[200px]">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-linepe-orange"></div>
            </div>
          )}

      {/* ========== DASHBOARD TAB ========== */}
      {!loading && activeTab === 'dashboard' && dashboard && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard title="Total Users" value={dashboard.users?.totalUsers} icon="👥" subtitle={`+${dashboard.users?.activeToday || 0} today`} color="blue" />
            <StatCard title="Points Earned" value={dashboard.points?.totalEarned} icon="📈" subtitle="Lifetime" color="green" />
            <StatCard title="Points Redeemed" value={dashboard.points?.totalRedeemed} icon="🛒" subtitle={`₹${formatNumber(dashboard.redemptions?.totalValue)} value`} color="purple" />
            <StatCard title="Transactions" value={dashboard.transactions?.todayCount} icon="📊" subtitle={`${dashboard.transactions?.monthCount || 0} this month`} color="orange" />
          </div>
          
          {activity && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">Today's Activity</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{activity.earnTransactions || 0}</p>
                  <p className="text-xs text-gray-600">Earn Txns</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{formatNumber(activity.earnPoints)}</p>
                  <p className="text-xs text-gray-600">Points Earned</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{activity.redeemTransactions || 0}</p>
                  <p className="text-xs text-gray-600">Redeem Txns</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-600">{formatNumber(activity.expiredPoints)}</p>
                  <p className="text-xs text-gray-600">Expired</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xl font-bold text-purple-600">{activity.newUsers || 0}</p>
                  <p className="text-xs text-gray-600">New Users</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">System Status</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><span className="text-gray-500">Config Version:</span> <span className="font-medium">{dashboard.system?.configVersion}</span></div>
              <div><span className="text-gray-500">Uptime:</span> <span className="font-medium">{dashboard.system?.uptime}</span></div>
              <div><span className="text-gray-500">Pending Orders:</span> <span className="font-medium">{dashboard.redemptions?.pendingOrders}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ========== USERS TAB ========== */}
      {!loading && activeTab === 'users' && (
        <div className="space-y-4">
          {/* Header with stats */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Points System Users</h2>
              <p className="text-sm text-gray-500">Users in the new Points system (Cosmos DB) - includes migrated users</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => loadTabData('users')} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
                🔄 Refresh
              </button>
              <button onClick={() => { setAdjustUserId(''); setShowAdjustModal(true); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Adjust Points</button>
            </div>
          </div>

          {/* Search bar */}
          <div className="bg-white p-4 rounded-lg shadow flex gap-4 items-center">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="Search by user ID or name..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-linepe-orange pl-10"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
            </div>
            <button onClick={() => handleSearchUsers()} className="px-6 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600">Search</button>
            {userSearch && (
              <button onClick={() => { setUserSearch(''); handleSearchUsers(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50">Clear</button>
            )}
          </div>

          {users.length > 0 ? (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Points</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Activity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-sm">{user.userId}</td>
                      <td className="px-4 py-3 font-medium">
                        {userNameMap.get(user.userId?.toString()) || (
                          <span className="text-gray-400 italic">Unknown</span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-right">{formatNumber(user.totalPoints)}</td>
                      <td className="px-4 py-3 text-green-600 font-semibold text-right">{formatNumber(user.availablePoints)}</td>
                      <td className="px-4 py-3 text-center">
                        {user.isMigrated ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            🔄 Migrated
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            💰 Earned
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(user.lastActivityAt)}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleViewUser(user.userId)} className="text-linepe-orange hover:underline mr-3">View</button>
                        <button onClick={() => { setAdjustUserId(user.userId); setShowAdjustModal(true); }} className="text-green-600 hover:underline">Adjust</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-600 flex justify-between">
                <span>Total: {users.length} users</span>
                <span>
                  {users.filter(u => u.isMigrated).length} migrated, {users.filter(u => !u.isMigrated).length} earned
                </span>
              </div>
            </div>
          ) : (
            <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
              <div className="text-4xl mb-3">👥</div>
              <p className="mb-2 font-medium">No users in Points system yet</p>
              <p className="text-sm mb-4">Users appear here after they earn points or are migrated from the legacy system</p>
              <button onClick={() => loadTabData('users')} className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600">
                Refresh
              </button>
            </div>
          )}

          {/* User Details Modal */}
          {selectedUser && (
            <Modal title={`User: ${selectedUser.userId}`} onClose={() => setSelectedUser(null)}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Points</p>
                  <p className="text-2xl font-bold">{formatNumber(selectedUser.balance?.totalPoints)}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Available</p>
                  <p className="text-2xl font-bold text-green-600">{formatNumber(selectedUser.balance?.availablePoints)}</p>
                </div>
              </div>
              <h4 className="font-semibold mb-2">Lifetime Stats</h4>
              <div className="grid grid-cols-4 gap-2 text-sm mb-4">
                <div className="p-2 bg-green-50 rounded text-center">
                  <p className="font-bold text-green-600">{formatNumber(selectedUser.balance?.lifetime?.earned)}</p>
                  <p className="text-xs">Earned</p>
                </div>
                <div className="p-2 bg-blue-50 rounded text-center">
                  <p className="font-bold text-blue-600">{formatNumber(selectedUser.balance?.lifetime?.redeemed)}</p>
                  <p className="text-xs">Redeemed</p>
                </div>
                <div className="p-2 bg-red-50 rounded text-center">
                  <p className="font-bold text-red-600">{formatNumber(selectedUser.balance?.lifetime?.expired)}</p>
                  <p className="text-xs">Expired</p>
                </div>
                <div className="p-2 bg-purple-50 rounded text-center">
                  <p className="font-bold text-purple-600">{formatNumber(selectedUser.balance?.lifetime?.adjusted)}</p>
                  <p className="text-xs">Adjusted</p>
                </div>
              </div>
              {selectedUser.balance?.migration?.isMigrated && (
                <div className="p-3 bg-yellow-50 rounded-lg text-sm">
                  <p className="font-medium">Migrated User</p>
                  <p className="text-gray-600">Original: ₹{selectedUser.balance.migration.migratedFromRupees} → {formatNumber(selectedUser.balance.migration.migratedPoints)} pts</p>
                </div>
              )}
            </Modal>
          )}
        </div>
      )}

      {/* ========== EARNING TAB ========== */}
      {!loading && activeTab === 'earning' && earningConfig && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Earning Tiers</h3>
              <div className="flex gap-2">
                <span className={`px-3 py-1 rounded-full text-sm ${earningConfig.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {earningConfig.isActive ? 'Active' : 'Inactive'}
                </span>
                <button onClick={() => openEditModal('earning-tiers', earningConfig)} className="px-3 py-1 bg-linepe-orange text-white rounded text-sm hover:bg-orange-600">Edit Tiers</button>
              </div>
            </div>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Tier Name</th>
                  <th className="text-left py-2">Min Amount (₹)</th>
                  <th className="text-left py-2">Max Amount (₹)</th>
                  <th className="text-left py-2">Points per ₹1000</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {earningConfig.tiers?.map((tier: any) => (
                  <tr key={tier.id} className="border-b">
                    <td className="py-3 font-medium">{tier.name}</td>
                    <td className="py-3">₹{formatNumber(tier.minAmount)}</td>
                    <td className="py-3">₹{tier.maxAmount ? formatNumber(tier.maxAmount) : '∞'}</td>
                    <td className="py-3 font-bold text-green-600">{tier.pointsPerThousand}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded text-xs ${tier.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">🎁 Welcome Bonus</h3>
                <button onClick={() => openEditModal('welcome-bonus', earningConfig)} className="px-3 py-1 bg-linepe-orange text-white rounded text-sm hover:bg-orange-600">Edit</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Enabled</span>
                  <span className={earningConfig.welcomeBonus?.enabled ? 'text-green-600 font-bold' : 'text-red-600'}>{earningConfig.welcomeBonus?.enabled ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Bonus Points</span>
                  <span className="font-bold text-xl text-orange-600">{formatNumber(earningConfig.welcomeBonus?.points)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Validity</span>
                  <span>{earningConfig.welcomeBonus?.validityDays} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Min Invoice</span>
                  <span>₹{formatNumber(earningConfig.welcomeBonus?.minInvoiceAmount)}</span>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">🚀 Bonus Multiplier</h3>
                <button onClick={() => openEditModal('bonus-multiplier', earningConfig)} className="px-3 py-1 bg-linepe-orange text-white rounded text-sm hover:bg-orange-600">Edit</button>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Enabled</span>
                  <span className={earningConfig.bonusMultiplier?.enabled ? 'text-green-600 font-bold' : 'text-red-600'}>{earningConfig.bonusMultiplier?.enabled ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Multiplier</span>
                  <span className="font-bold text-xl text-purple-600">{earningConfig.bonusMultiplier?.multiplier}x</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Start Date</span>
                  <span>{earningConfig.bonusMultiplier?.startDate ? formatDate(earningConfig.bonusMultiplier.startDate) : 'Not set'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">End Date</span>
                  <span>{earningConfig.bonusMultiplier?.endDate ? formatDate(earningConfig.bonusMultiplier.endDate) : 'Not set'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">📊 Earning Limits</h3>
              <button onClick={() => openEditModal('earning-limits', earningConfig)} className="px-3 py-1 bg-linepe-orange text-white rounded text-sm hover:bg-orange-600">Edit</button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">{formatNumber(earningConfig.limits?.maxPointsPerInvoice)}</p>
                <p className="text-sm text-gray-600">Max per Invoice</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">{formatNumber(earningConfig.limits?.maxPointsPerDay)}</p>
                <p className="text-sm text-gray-600">Max per Day</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">{formatNumber(earningConfig.limits?.maxPointsPerMonth)}</p>
                <p className="text-sm text-gray-600">Max per Month</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== EXPIRY TAB ========== */}
      {!loading && activeTab === 'expiry' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Expiry Management</h3>
            <button onClick={handleRunExpiryJob} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
              <span>🔄</span> Run Expiry Job Now
            </button>
          </div>

          {expiryStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard title="Expiring (7 days)" value={expiryStats.pointsExpiringNext7Days} icon="⚠️" color="yellow" />
              <StatCard title="Expiring (30 days)" value={expiryStats.pointsExpiringNext30Days} icon="📅" color="orange" />
              <StatCard title="Expiring (90 days)" value={expiryStats.pointsExpiringNext90Days} icon="📆" color="blue" />
              <StatCard title="Users Affected" value={expiryStats.totalUsersWithExpiringPoints} icon="👥" color="purple" />
            </div>
          )}

          {expiryConfig && (
            <>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Expiry Rules by Point Type</h3>
                  <div className="flex gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm ${expiryConfig.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {expiryConfig.enabled ? 'Expiry Enabled' : 'Expiry Disabled'}
                    </span>
                    <button onClick={() => openEditModal('expiry-rules', expiryConfig)} className="px-3 py-1 bg-linepe-orange text-white rounded text-sm hover:bg-orange-600">Edit Rules</button>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
                  {Object.entries(expiryConfig.rules || {}).map(([type, rule]: [string, any]) => (
                    <div key={type} className="p-4 bg-gray-50 rounded-lg text-center">
                      <p className="font-medium capitalize text-sm mb-2">{type.replace('_', ' ')}</p>
                      <p className="text-xl font-bold text-red-600">{rule.expiryDays}d</p>
                      <p className="text-xs text-gray-500">+{rule.gracePeriodDays}d grace</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-4">Expiry Notifications</h3>
                <div className="flex gap-4">
                  {expiryConfig.notifications?.schedule?.map((sched: any, idx: number) => (
                    <div key={idx} className="p-4 bg-blue-50 rounded-lg text-center flex-1">
                      <p className="text-2xl font-bold text-blue-600">{sched.daysBeforeExpiry}d</p>
                      <p className="text-sm text-gray-600">before expiry</p>
                      <p className="text-xs text-gray-500 mt-1">{sched.channels?.join(', ')}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="font-semibold mb-4">Batch Processing</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-gray-500">Enabled:</span> <span className={expiryConfig.batchProcessing?.enabled ? 'text-green-600 font-bold' : 'text-red-600'}>{expiryConfig.batchProcessing?.enabled ? 'Yes' : 'No'}</span></div>
                  <div><span className="text-gray-500">Run Time (UTC):</span> <span className="font-medium">{expiryConfig.batchProcessing?.runTimeUTC}</span></div>
                  <div><span className="text-gray-500">Batch Size:</span> <span className="font-medium">{expiryConfig.batchProcessing?.batchSize}</span></div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== REDEMPTION TAB ========== */}
      {!loading && activeTab === 'redemption' && redemptionConfig && (
        <div className="space-y-6">
          <div className="flex justify-end mb-2">
            <button onClick={() => openEditModal('redemption', redemptionConfig)} className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600">Edit Redemption Settings</button>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">💱 Conversion Rate</h3>
              <p className="text-3xl font-bold text-green-600">{redemptionConfig.conversionRate?.pointsPerRupee} pts = ₹1</p>
              <p className="text-sm text-gray-500 mt-2">Effective: {formatDate(redemptionConfig.conversionRate?.effectiveFrom)}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">📉 Minimums</h3>
              <div className="space-y-2">
                <p><span className="text-gray-500">Min Points:</span> <span className="font-bold">{formatNumber(redemptionConfig.minimums?.minPointsToRedeem)}</span></p>
                <p><span className="text-gray-500">Min Amount:</span> <span className="font-bold">₹{redemptionConfig.minimums?.minTransactionAmount}</span></p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">📊 Default Split</h3>
              <div className="space-y-2">
                <p><span className="text-gray-500">Max Points:</span> <span className="font-bold text-green-600">{redemptionConfig.splitStrategy?.default?.maxPointsPercentage}%</span></p>
                <p><span className="text-gray-500">Min Cash:</span> <span className="font-bold text-blue-600">{redemptionConfig.splitStrategy?.default?.minCashPercentage}%</span></p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">📦 Redemption Categories</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Category</th>
                  <th className="text-left py-2">Max Points %</th>
                  <th className="text-left py-2">Min Cash %</th>
                  <th className="text-left py-2">Amount Range</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {redemptionConfig.categories?.map((cat: any) => (
                  <tr key={cat.id} className="border-b">
                    <td className="py-3 font-medium">{cat.name}</td>
                    <td className="py-3 text-green-600 font-bold">{cat.maxPointsPercentage}%</td>
                    <td className="py-3 text-blue-600">{cat.minCashPercentage}%</td>
                    <td className="py-3">₹{cat.minAmount} - ₹{formatNumber(cat.maxAmount)}</td>
                    <td className="py-3">
                      <span className={`px-2 py-1 rounded text-xs ${cat.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {redemptionConfig.valueModifiers?.enabled && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">💎 Value Modifiers</h3>
              <div className="grid grid-cols-4 gap-4">
                {redemptionConfig.valueModifiers?.slabs?.map((slab: any, idx: number) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg text-center">
                    <p className="text-sm text-gray-500">Up to ₹{slab.upTo || '∞'}</p>
                    <p className={`text-xl font-bold ${slab.maxPointsBonus >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {slab.maxPointsBonus >= 0 ? '+' : ''}{slab.maxPointsBonus}%
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ========== CATALOG TAB ========== */}
      {!loading && activeTab === 'catalog' && (
        <div className="space-y-6">
          {/* Empty State - Show when no catalog data exists */}
          {categories.length === 0 && operators.length === 0 && (
            <div className="bg-white p-8 rounded-lg shadow">
              <div className="text-center max-w-lg mx-auto">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">📦</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Catalog is Empty</h3>
                <p className="text-gray-500 mb-6">
                  Your redemption catalog doesn't have any categories or operators yet. 
                  To populate the catalog, configure a BBPS provider and sync the catalog data.
                </p>
                <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">How to populate the catalog:</h4>
                  <ol className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>Go to <strong>BBPS Gateway</strong> tab</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span>Configure <strong>PayU</strong> or <strong>Razorpay</strong> BBPS credentials</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="bg-orange-500 text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span>Click <strong>Sync Now</strong> to fetch categories, operators, and plans</span>
                    </li>
                  </ol>
                </div>
                <button
                  onClick={() => setActiveTab('bbps')}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  Go to BBPS Gateway
                </button>
              </div>
            </div>
          )}

          {/* Setup Tip - Show when catalog partially synced or needs operators */}
          {(categories.length > 0 || operators.length > 0) && operators.length === 0 && !catalogTipDismissed && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <span className="text-2xl">💡</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-blue-800">Setup Tip: Complete Your Catalog</h4>
                    <button 
                      onClick={() => setCatalogTipDismissed(true)}
                      className="text-blue-400 hover:text-blue-600 text-lg font-bold"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-sm text-blue-700 mt-1 mb-2">
                    Your catalog has categories but no operators yet. To sync operators and plans:
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-blue-600">
                      <strong>1.</strong> Go to <strong>BBPS Gateway</strong>
                    </span>
                    <span className="text-blue-400">→</span>
                    <span className="text-blue-600">
                      <strong>2.</strong> Configure a provider
                    </span>
                    <span className="text-blue-400">→</span>
                    <span className="text-blue-600">
                      <strong>3.</strong> Click <strong>Sync Now</strong>
                    </span>
                    <button
                      onClick={() => setActiveTab('bbps')}
                      className="ml-auto bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                    >
                      Go to BBPS Gateway
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Categories Section */}
          {(categories.length > 0 || operators.length > 0) && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">Categories ({categories.length})</h3>
              {categories.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {categories.map((cat) => (
                    <div key={cat.id} className="p-4 border rounded-lg hover:border-orange-300 hover:bg-orange-50 transition-colors">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{cat.icon === 'phone' ? '📱' : cat.icon === 'electricity' ? '💡' : cat.icon === 'water' ? '💧' : cat.icon === 'gas' ? '🔥' : '📦'}</span>
                        <span className="font-medium">{cat.displayName || cat.name}</span>
                        {cat.featured && <span className="ml-auto px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs">Featured</span>}
                      </div>
                      <p className="text-sm text-gray-500">{cat.operatorCount || 0} operators</p>
                      <p className="text-sm text-gray-500">{cat.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <span className="text-4xl mb-2 block">📂</span>
                  <p className="text-gray-500">No categories synced yet</p>
                  <p className="text-sm text-gray-400">Run a catalog sync from the BBPS Gateway tab</p>
                </div>
              )}
            </div>
          )}

          {/* Operators Section */}
          {(categories.length > 0 || operators.length > 0) && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">Operators ({operators.length})</h3>
              {operators.length > 0 ? (
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Name</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Category</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Plans</th>
                      <th className="text-left py-3 px-2 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {operators.map((op) => (
                      <tr key={op.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2 font-medium">{op.displayName || op.name}</td>
                        <td className="py-3 px-2">{op.categoryId}</td>
                        <td className="py-3 px-2">{op.planCount || 0}</td>
                        <td className="py-3 px-2">
                          <span className={`px-2 py-1 rounded text-xs ${op.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {op.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <span className="text-4xl mb-2 block">🏢</span>
                  <p className="text-gray-500">No operators synced yet</p>
                  <p className="text-sm text-gray-400">Operators will appear after catalog sync</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== ORDERS TAB ========== */}
      {!loading && activeTab === 'orders' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {orders.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Points</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs">{order.id?.substring(0, 12)}...</td>
                    <td className="px-4 py-3">{order.userId}</td>
                    <td className="px-4 py-3">{order.categoryName || order.categoryId}</td>
                    <td className="px-4 py-3">₹{formatNumber(order.amount)}</td>
                    <td className="px-4 py-3">{formatNumber(order.pointsUsed)}</td>
                    <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                    <td className="px-4 py-3">
                      {order.status === 'processing' && (
                        <button onClick={() => handleCompleteOrder(order.id)} className="text-green-600 hover:underline text-sm">Complete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="p-8 text-center text-gray-500">No redemption orders found</p>
          )}
        </div>
      )}

      {/* ========== MIGRATION TAB ========== */}
      {!loading && activeTab === 'migration' && (
        <div className="flex h-[calc(100vh-180px)] -m-6">
          {/* Migration Sub-Sidebar - Collapsible */}
          <div className={`${migrationSubSidebarCollapsed ? 'w-12' : 'w-52'} bg-white border-r overflow-y-auto flex-shrink-0 transition-all duration-200 relative`}>
            {/* Collapse Button */}
            <button
              onClick={() => setMigrationSubSidebarCollapsed(!migrationSubSidebarCollapsed)}
              className="absolute top-2 right-1 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors z-10 text-xs"
              title={migrationSubSidebarCollapsed ? 'Expand' : 'Collapse'}
            >
              {migrationSubSidebarCollapsed ? '»' : '«'}
            </button>

            <nav className={`${migrationSubSidebarCollapsed ? 'pt-8' : 'pt-8'} pb-2`}>
              <button
                onClick={() => setMigrationSubView('batches')}
                title={migrationSubSidebarCollapsed ? 'Migration Batches' : undefined}
                className={`w-full flex items-center ${migrationSubSidebarCollapsed ? 'justify-center px-2' : 'gap-2 px-3'} py-2 text-sm font-medium transition-colors text-left border-l-2 ${
                  migrationSubView === 'batches'
                    ? 'border-linepe-orange bg-orange-50 text-linepe-orange'
                    : 'border-transparent text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>📦</span>
                {!migrationSubSidebarCollapsed && <span>Batches</span>}
              </button>
              <button
                onClick={() => { setMigrationSubView('sql-users'); if (sqlUsers.length === 0) fetchSqlUsers(); }}
                title={migrationSubSidebarCollapsed ? 'SQL Server Users' : undefined}
                className={`w-full flex items-center ${migrationSubSidebarCollapsed ? 'justify-center px-2' : 'gap-2 px-3'} py-2 text-sm font-medium transition-colors text-left border-l-2 ${
                  migrationSubView === 'sql-users'
                    ? 'border-linepe-orange bg-orange-50 text-linepe-orange'
                    : 'border-transparent text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>🗄️</span>
                {!migrationSubSidebarCollapsed && <span>SQL Users</span>}
              </button>
            </nav>

            {/* SQL Users Count Badge */}
            {!migrationSubSidebarCollapsed && migrationSubView === 'sql-users' && sqlUsers.length > 0 && (
              <div className="px-3 py-2 text-xs text-gray-500 border-t">
                {sqlUsers.length} users from SQL
              </div>
            )}
          </div>

          {/* Migration Content Area */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Batches View */}
            {migrationSubView === 'batches' && (
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex justify-end gap-3">
                  <button onClick={() => openEditModal('migration', migrationConfig)} className="px-4 py-2 border border-linepe-orange text-linepe-orange rounded-lg hover:bg-orange-50">Edit Configuration</button>
                  <button onClick={() => setShowNewBatchModal(true)} className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 flex items-center gap-2">
                    <span>+</span> Create New Migration Batch
                  </button>
                </div>

                {migrationStats && (
                  <div className="grid grid-cols-4 gap-4">
                    <StatCard title="Total Batches" value={migrationStats.totalBatches} icon="📦" color="blue" />
                    <StatCard title="Completed" value={migrationStats.completedBatches} icon="✅" color="green" />
                    <StatCard title="Users Migrated" value={migrationStats.totalUsersMigrated} icon="👥" color="purple" />
                    <StatCard title="Points Migrated" value={migrationStats.totalPointsMigrated} icon="💰" color="orange" />
                  </div>
                )}

                {migrationConfig && (
                  <div className="bg-white p-6 rounded-lg shadow">
                    <h3 className="font-semibold mb-4">Migration Configuration</h3>
                    <div className="grid grid-cols-4 gap-4">
                      <div><span className="text-gray-500">Enabled:</span> <span className={migrationConfig.settings?.enabled ? 'text-green-600 font-bold' : 'text-red-600'}>{migrationConfig.settings?.enabled ? 'Yes' : 'No'}</span></div>
                      <div><span className="text-gray-500">Conversion:</span> <span className="font-bold">₹1 = {migrationConfig.settings?.conversionRate} pts</span></div>
                      <div><span className="text-gray-500">Mode:</span> <span className="font-medium capitalize">{migrationConfig.settings?.mode}</span></div>
                      <div><span className="text-gray-500">Rollback:</span> <span className={migrationConfig.rollback?.enabled ? 'text-green-600' : 'text-red-600'}>{migrationConfig.rollback?.enabled ? 'Enabled' : 'Disabled'}</span></div>
                    </div>
                  </div>
                )}

                <div className="bg-white p-6 rounded-lg shadow">
                  <h3 className="font-semibold mb-4">Migration Batches</h3>
                  {migrationBatches.length > 0 ? (
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Batch Name</th>
                          <th className="text-left py-2">Status</th>
                          <th className="text-left py-2">Users</th>
                          <th className="text-left py-2">Points</th>
                          <th className="text-left py-2">Executed By</th>
                          <th className="text-left py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {migrationBatches.map((batch) => (
                          <tr key={batch.id} className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => handleViewBatchDetails(batch)}>
                            <td className="py-2 font-medium text-linepe-orange hover:underline">{batch.name}</td>
                            <td className="py-2"><StatusBadge status={batch.status} /></td>
                            <td className="py-2">{batch.stats?.processedUsers || 0} / {batch.stats?.totalUsers || 0}</td>
                            <td className="py-2">{formatNumber(batch.stats?.totalPointsMigrated || 0)}</td>
                            <td className="py-2">{batch.executedBy || '-'}</td>
                            <td className="py-2 space-x-2" onClick={(e) => e.stopPropagation()}>
                              {batch.status === 'pending' && (
                                <button onClick={() => handleExecuteBatch(batch.id)} className="text-green-600 hover:underline text-sm">Execute</button>
                              )}
                              {batch.status === 'completed' && (
                                <button onClick={() => handleRollbackBatch(batch.id)} className="text-red-600 hover:underline text-sm">Rollback</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No migration batches</p>
                  )}
                </div>
              </div>
            )}

            {/* SQL Users View */}
            {migrationSubView === 'sql-users' && (
              <div className="space-y-4">
                {/* Migration Status Summary */}
                {sqlUsers.length > 0 && (
                  <div className="flex gap-4 p-3 bg-gray-50 rounded-lg text-sm">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-gray-300"></span>
                      <span>Pending: {sqlUsers.filter(u => !migratedUsersMap.has(u.userId?.toString())).length}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full bg-green-500"></span>
                      <span>Migrated: {sqlUsers.filter(u => migratedUsersMap.has(u.userId?.toString())).length}</span>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showMigratedUsers}
                          onChange={(e) => setShowMigratedUsers(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-gray-600">Show migrated</span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">SQL Server Users</h2>
                    <p className="text-sm text-gray-500">Users with reward points from the legacy SQL database</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Fetch Limit Dropdown */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-600">Show:</label>
                      <select
                        value={sqlUsersFetchLimit}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === 'all' || val === 'custom') {
                            setSqlUsersFetchLimit(val);
                          } else {
                            setSqlUsersFetchLimit(Number(val));
                          }
                        }}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-linepe-orange focus:border-linepe-orange"
                      >
                        <option value={10}>Top 10</option>
                        <option value={25}>Top 25</option>
                        <option value={50}>Top 50</option>
                        <option value={100}>Top 100</option>
                        <option value="all">All Users</option>
                        <option value="custom">Custom...</option>
                      </select>
                      {/* Custom input field */}
                      {sqlUsersFetchLimit === 'custom' && (
                        <input
                          type="number"
                          min="1"
                          placeholder="Enter number"
                          value={sqlUsersCustomLimit}
                          onChange={(e) => setSqlUsersCustomLimit(e.target.value)}
                          className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-linepe-orange focus:border-linepe-orange"
                        />
                      )}
                    </div>
                    <button
                      onClick={() => fetchSqlUsers()}
                      disabled={sqlUsersLoading || (sqlUsersFetchLimit === 'custom' && !sqlUsersCustomLimit)}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                    >
                      {sqlUsersLoading ? '⏳' : '🔄'} Refresh
                    </button>
                    {selectedSqlUsers.size > 0 && (
                      <button
                        onClick={addSelectedUsersToNewBatch}
                        className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 flex items-center gap-2"
                      >
                        <span>+</span> Create Batch ({selectedSqlUsers.size})
                      </button>
                    )}
                  </div>
                </div>

                {sqlUsersLoading ? (
                  <div className="bg-white p-8 rounded-lg shadow text-center">
                    <div className="animate-spin text-4xl mb-2">⏳</div>
                    <p className="text-gray-500">Fetching users from SQL Server...</p>
                  </div>
                ) : sqlUsers.length > 0 ? (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-3 px-4 w-10">
                            <input
                              type="checkbox"
                              checked={selectedSqlUsers.size === sqlUsers.filter(u => 
                                !sqlUsersSearchTerm || u.name?.toLowerCase().includes(sqlUsersSearchTerm.toLowerCase())
                              ).length && sqlUsers.length > 0}
                              onChange={selectAllSqlUsers}
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="text-left py-3 px-4">User ID</th>
                          <th className="text-left py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span>Name</span>
                              <input
                                type="text"
                                placeholder="🔍 Search..."
                                value={sqlUsersSearchTerm}
                                onChange={(e) => setSqlUsersSearchTerm(e.target.value)}
                                className="px-2 py-1 text-sm font-normal border border-gray-300 rounded w-40 focus:ring-2 focus:ring-linepe-orange focus:border-linepe-orange"
                              />
                              {sqlUsersSearchTerm && (
                                <button
                                  onClick={() => setSqlUsersSearchTerm('')}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Clear search"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </th>
                          <th className="text-right py-3 px-4">Legacy Rewards (₹)</th>
                          <th className="text-center py-3 px-4">Migration Status</th>
                          <th className="text-center py-3 px-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sqlUsers
                          .filter(user => showMigratedUsers || !migratedUsersMap.has(user.userId?.toString()))
                          .filter(user => !sqlUsersSearchTerm || user.name?.toLowerCase().includes(sqlUsersSearchTerm.toLowerCase()))
                          .map((user, idx) => {
                            const isMigrated = migratedUsersMap.has(user.userId?.toString());
                            return (
                          <tr key={user.userId} className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} ${isMigrated ? 'opacity-60' : ''}`}>
                            <td className="py-3 px-4">
                              <input
                                type="checkbox"
                                checked={selectedSqlUsers.has(user.userId)}
                                onChange={() => toggleSqlUserSelection(user.userId)}
                                disabled={isMigrated}
                                className={`rounded border-gray-300 ${isMigrated ? 'cursor-not-allowed' : ''}`}
                                title={isMigrated ? 'Already migrated' : 'Select for migration'}
                              />
                            </td>
                            <td className="py-3 px-4 font-mono text-xs">{user.userId}</td>
                            <td className="py-3 px-4 font-medium">{user.name}</td>
                            <td className="py-3 px-4 text-right font-semibold text-green-600">
                              ₹{formatNumber(user.totalRewardAmount)}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {migratedUsersMap.has(user.userId?.toString()) ? (
                                <div className="flex flex-col items-center">
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    ✓ Migrated
                                  </span>
                                  <span className="text-xs text-gray-500 mt-0.5">
                                    {formatNumber(migratedUsersMap.get(user.userId?.toString())?.balance || 0)} pts
                                  </span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  Pending
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {migratedUsersMap.has(user.userId?.toString()) ? (
                                <span className="text-gray-400 text-xs cursor-not-allowed" title="Already migrated">
                                  —
                                </span>
                              ) : (
                                <button
                                  onClick={() => {
                                    setNewBatchUserIds(user.userId);
                                    setNewBatchName(`Single user migration - ${user.name}`);
                                    setPendingMigrationUsers([{
                                      userId: user.userId,
                                      legacyPoints: user.totalRewardAmount || 0,
                                      name: user.name
                                    }]);
                                    setShowNewBatchModal(true);
                                  }}
                                  className="text-linepe-orange hover:underline text-xs font-medium"
                                >
                                  Migrate
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                        })}
                      </tbody>
                    </table>
                    <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-600 flex justify-between items-center">
                      <span>
                        {(() => {
                          const filtered = sqlUsers
                            .filter(u => showMigratedUsers || !migratedUsersMap.has(u.userId?.toString()))
                            .filter(u => !sqlUsersSearchTerm || u.name?.toLowerCase().includes(sqlUsersSearchTerm.toLowerCase()));
                          const pending = filtered.filter(u => !migratedUsersMap.has(u.userId?.toString())).length;
                          return `Showing ${filtered.length} users (${pending} pending migration)`;
                        })()}
                      </span>
                      <span>Pending Rewards: ₹{formatNumber(
                        sqlUsers
                          .filter(u => !migratedUsersMap.has(u.userId?.toString()))
                          .filter(u => !sqlUsersSearchTerm || u.name?.toLowerCase().includes(sqlUsersSearchTerm.toLowerCase()))
                          .reduce((sum, u) => sum + (u.totalRewardAmount || 0), 0)
                      )}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white p-8 rounded-lg shadow text-center">
                    <div className="text-4xl mb-2">🗄️</div>
                    <p className="text-gray-500">No users found in SQL database</p>
                    <button
                      onClick={() => fetchSqlUsers()}
                      className="mt-4 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600"
                    >
                      Load SQL Users
                    </button>
                  </div>
                )}

                {/* Info Card */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2">💡 How Migration Works</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Select users from the list to create a migration batch</li>
                    <li>• Points are converted using the configured rate (₹1 = {migrationConfig?.settings?.conversionRate || 1} pts)</li>
                    <li>• Migration can be previewed before execution</li>
                    <li>• Completed migrations can be rolled back if enabled</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== NOTIFICATIONS TAB ========== */}
      {!loading && activeTab === 'notifications' && notificationConfig && (
        <div className="space-y-6">
          <div className="flex justify-end mb-2">
            <button onClick={() => openEditModal('notifications', notificationConfig)} className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600">Edit Notification Settings</button>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">📢 Notification Status</h3>
                <span className={`px-3 py-1 rounded-full text-sm ${notificationConfig.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {notificationConfig.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p><span className="text-gray-500">Default Language:</span> <span className="font-medium">{notificationConfig.defaultLanguage}</span></p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">🌙 Quiet Hours</h3>
              <div className="space-y-2">
                <p><span className="text-gray-500">Enabled:</span> <span className={notificationConfig.quietHours?.enabled ? 'text-green-600 font-bold' : 'text-red-600'}>{notificationConfig.quietHours?.enabled ? 'Yes' : 'No'}</span></p>
                <p><span className="text-gray-500">Time:</span> <span className="font-medium">{notificationConfig.quietHours?.startTime} - {notificationConfig.quietHours?.endTime}</span></p>
                <p><span className="text-gray-500">Timezone:</span> <span>{notificationConfig.quietHours?.timezone}</span></p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">📨 Notification Events ({notificationConfig.events?.length || 0})</h3>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Event</th>
                  <th className="text-left py-2">Description</th>
                  <th className="text-left py-2">Channels</th>
                  <th className="text-left py-2">Priority</th>
                  <th className="text-left py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {notificationConfig.events?.map((event: any) => (
                  <tr key={event.id} className="border-b">
                    <td className="py-2 font-medium">{event.name}</td>
                    <td className="py-2 text-gray-500 text-xs">{event.description}</td>
                    <td className="py-2">{event.channels?.join(', ')}</td>
                    <td className="py-2"><StatusBadge status={event.priority} /></td>
                    <td className="py-2">
                      <span className={`px-2 py-1 rounded text-xs ${event.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                        {event.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">📝 Notification Templates ({notificationConfig.templates?.length || 0})</h3>
            <div className="space-y-3">
              {notificationConfig.templates?.map((tpl: any) => (
                <div key={tpl.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium">{tpl.name}</span>
                    <span className="text-xs text-gray-500">{tpl.language}</span>
                  </div>
                  <p className="text-sm text-gray-600 font-mono">{tpl.whatsapp?.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ========== PAYMENT TAB ========== */}
      {!loading && activeTab === 'payment' && paymentConfig && (
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Active Gateway</h3>
              <button onClick={() => openEditModal('payment', paymentConfig)} className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600">Edit Payment Settings</button>
            </div>
            <p className="text-3xl font-bold capitalize text-blue-600">{paymentConfig.activeGateway || 'None'}</p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">💳 Razorpay</h3>
                <span className={`px-3 py-1 rounded-full text-sm ${paymentConfig.razorpay?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {paymentConfig.razorpay?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="space-y-2">
                <p><span className="text-gray-500">Mode:</span> <span className={paymentConfig.razorpay?.mode === 'live' ? 'text-green-600 font-bold' : 'text-yellow-600'}>{paymentConfig.razorpay?.mode}</span></p>
                <p><span className="text-gray-500">Key ID:</span> <span className="font-mono text-sm">{paymentConfig.razorpay?.keyId || 'Not configured'}</span></p>
                <p><span className="text-gray-500">Webhook:</span> <span className={paymentConfig.razorpay?.webhookEnabled ? 'text-green-600' : 'text-gray-500'}>{paymentConfig.razorpay?.webhookEnabled ? 'Enabled' : 'Disabled'}</span></p>
                <p><span className="text-gray-500">BBPS:</span> <span className={paymentConfig.razorpay?.bbps?.enabled ? 'text-green-600' : 'text-gray-500'}>{paymentConfig.razorpay?.bbps?.enabled ? 'Enabled' : 'Disabled'}</span></p>
              </div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold">💳 PayU</h3>
                <span className={`px-3 py-1 rounded-full text-sm ${paymentConfig.payu?.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {paymentConfig.payu?.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="space-y-2">
                <p><span className="text-gray-500">Mode:</span> <span className={paymentConfig.payu?.mode === 'live' ? 'text-green-600 font-bold' : 'text-yellow-600'}>{paymentConfig.payu?.mode}</span></p>
                <p><span className="text-gray-500">Merchant Key:</span> <span className="font-mono text-sm">{paymentConfig.payu?.merchantKey || 'Not configured'}</span></p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">📊 Transaction Limits</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">₹{formatNumber(paymentConfig.transactionLimits?.maxAmount)}</p>
                <p className="text-sm text-gray-600">Max Amount</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">{paymentConfig.transactionLimits?.maxPerDay}</p>
                <p className="text-sm text-gray-600">Max per Day</p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg text-center">
                <p className="text-2xl font-bold text-yellow-700">{paymentConfig.transactionLimits?.cooldownSeconds}s</p>
                <p className="text-sm text-gray-600">Cooldown</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">🔄 Retry Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><span className="text-gray-500">Max Attempts:</span> <span className="font-bold">{paymentConfig.retry?.maxAttempts}</span></div>
              <div><span className="text-gray-500">Backoff (ms):</span> <span className="font-mono">{paymentConfig.retry?.backoffMs?.join(', ')}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* ========== BBPS GATEWAY TAB ========== */}
      {!loading && activeTab === 'bbps' && (
        <div className="space-y-6">
          {/* Provider Status */}
          <div className="grid grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <span>🔌</span> Active Provider
              </h3>
              <p className="text-3xl font-bold capitalize text-blue-600">
                {bbpsConfig?.activeProvider || 'None'}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Fallback: {bbpsConfig?.fallbackProvider || 'None'}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <span>🔄</span> Catalog Sync
              </h3>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${bbpsSyncStatus?.status?.isScheduledActive ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="font-bold">{bbpsSyncStatus?.status?.isScheduledActive ? 'Active' : 'Inactive'}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Last sync: {bbpsSyncStatus?.status?.lastRunAt ? new Date(bbpsSyncStatus.status.lastRunAt).toLocaleString() : 'Never'}
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <span>💚</span> Health Status
              </h3>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${bbpsHealth ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="font-bold">{bbpsHealth ? 'Connected' : 'Not Initialized'}</span>
              </div>
              <div className="relative inline-block group mt-3">
                <button 
                  onClick={async () => {
                    try {
                      await api.initializeBBPSFactory();
                      const health = await api.getBBPSHealth();
                      setBbpsHealth((health as any).health || null);
                      setSuccessMsg('BBPS Factory initialized');
                    } catch (err: any) {
                      setError(err.message);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Initialize Factory
                </button>
                
                {/* Tooltip */}
                <div className="absolute left-0 top-full mt-2 w-72 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <p className="font-semibold mb-2">When to use:</p>
                  <ul className="space-y-1 text-gray-200">
                    <li>• After saving provider credentials</li>
                    <li>• After enabling/disabling a provider</li>
                    <li>• After changing Active/Fallback provider</li>
                    <li>• If status shows "Not Initialized"</li>
                  </ul>
                  <p className="mt-2 text-gray-400 italic">Safe to run anytime.</p>
                  <div className="absolute -top-1 left-6 w-2 h-2 bg-gray-900 rotate-45"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Providers List */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="font-semibold mb-4">Configured Providers</h3>
            <div className="grid grid-cols-2 gap-6">
              {/* Show default providers if API returns empty */}
              {(bbpsProviders.length > 0 ? bbpsProviders : [
                { id: 'payu', name: 'PayU BBPS', enabled: false, configured: false, environment: 'test', isActive: false, isFallback: false },
                { id: 'razorpay', name: 'Razorpay BBPS', enabled: false, configured: false, environment: 'test', isActive: false, isFallback: false },
              ]).map((provider: any) => (
                <div key={provider.id} className={`p-4 rounded-lg border-2 ${provider.isActive ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold text-lg">{provider.name}</h4>
                      <p className="text-sm text-gray-500">Environment: {provider.environment || 'Not set'}</p>
                    </div>
                    <div className="flex gap-2">
                      {provider.isActive && <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>}
                      {provider.isFallback && <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">Fallback</span>}
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className={provider.enabled ? 'text-green-600' : 'text-gray-400'}>
                      {provider.enabled ? '✓ Enabled' : '✗ Disabled'}
                    </span>
                    <span className={provider.configured ? 'text-green-600' : 'text-gray-400'}>
                      {provider.configured ? '✓ Configured' : '✗ Not Configured'}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setBbpsEditModal(provider.id as 'payu' | 'razorpay');
                        setBbpsEditData(provider.id === 'payu' ? {
                          enabled: bbpsConfig?.payu?.enabled || false,
                          environment: bbpsConfig?.payu?.environment || 'test',
                          clientId: bbpsConfig?.payu?.credentials?.clientId || '',
                          clientSecretRef: '',
                          agentId: bbpsConfig?.payu?.credentials?.agentId || '',
                          grantType: bbpsConfig?.payu?.credentials?.grantType || 'client_credentials',
                          authHost: bbpsConfig?.payu?.endpoints?.authHost || 'https://uat-accounts.payu.in',
                          nbcHost: bbpsConfig?.payu?.endpoints?.nbcHost || 'https://bbps-sb.payu.in',
                        } : {
                          enabled: bbpsConfig?.razorpay?.enabled || false,
                          environment: bbpsConfig?.razorpay?.environment || 'test',
                          keyId: bbpsConfig?.razorpay?.credentials?.keyId || '',
                          keySecretRef: '',
                          baseUrl: bbpsConfig?.razorpay?.endpoints?.baseUrl || 'https://api.razorpay.com',
                        });
                      }}
                      className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                    >
                      Configure
                    </button>
                    {!provider.isActive && provider.enabled && provider.configured && (
                      <button
                        onClick={async () => {
                          try {
                            await api.setActiveProvider(provider.id);
                            loadTabData('bbps');
                            setSuccessMsg(`${provider.name} set as active provider`);
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Set Active
                      </button>
                    )}
                    {!provider.isFallback && provider.enabled && provider.configured && !provider.isActive && (
                      <button
                        onClick={async () => {
                          try {
                            await api.setFallbackProvider(provider.id);
                            loadTabData('bbps');
                            setSuccessMsg(`${provider.name} set as fallback provider`);
                          } catch (err: any) {
                            setError(err.message);
                          }
                        }}
                        className="px-3 py-1 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
                      >
                        Set Fallback
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Catalog Sync Controls */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <span>📦</span> Catalog Sync
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      const result = await api.previewCatalogSync();
                      console.log('Sync preview:', result);
                      setSuccessMsg(`Preview: Would sync ${(result as any).preview?.categories?.fetched || 0} categories, ${(result as any).preview?.operators?.fetched || 0} operators, ${(result as any).preview?.plans?.fetched || 0} plans`);
                    } catch (err: any) {
                      setError(err.message);
                    }
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  disabled={bbpsSyncing}
                >
                  Preview Sync
                </button>
                <button
                  onClick={async () => {
                    setBbpsSyncing(true);
                    try {
                      const result = await api.triggerCatalogSync({ dryRun: false });
                      loadTabData('bbps');
                      setSuccessMsg(`Sync completed: ${(result as any).result?.categories?.created || 0} categories, ${(result as any).result?.operators?.created || 0} operators, ${(result as any).result?.plans?.created || 0} plans created`);
                    } catch (err: any) {
                      setError(err.message);
                    } finally {
                      setBbpsSyncing(false);
                    }
                  }}
                  className="px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                  disabled={bbpsSyncing || bbpsConfig?.activeProvider === 'none'}
                >
                  {bbpsSyncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Scheduled Sync</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={bbpsSyncStatus?.status?.isScheduledActive ? 'text-green-600 font-bold' : 'text-gray-500'}>
                    {bbpsSyncStatus?.status?.isScheduledActive ? 'Running' : 'Stopped'}
                  </span>
                  {bbpsSyncStatus?.status?.isScheduledActive ? (
                    <button
                      onClick={async () => {
                        try {
                          await api.stopScheduledSync();
                          loadTabData('bbps');
                          setSuccessMsg('Scheduled sync stopped');
                        } catch (err: any) {
                          setError(err.message);
                        }
                      }}
                      className="text-sm text-red-600 hover:underline"
                    >
                      Stop
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          await api.startScheduledSync(24);
                          loadTabData('bbps');
                          setSuccessMsg('Scheduled sync started (every 24 hours)');
                        } catch (err: any) {
                          setError(err.message);
                        }
                      }}
                      className="text-sm text-green-600 hover:underline"
                    >
                      Start (24h)
                    </button>
                  )}
                </div>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Last Sync</p>
                <p className="font-bold mt-2">
                  {bbpsSyncStatus?.lastResult?.completedAt 
                    ? new Date(bbpsSyncStatus.lastResult.completedAt).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Last Sync Result</p>
                <p className={`font-bold mt-2 ${bbpsSyncStatus?.lastResult?.success ? 'text-green-600' : 'text-gray-500'}`}>
                  {bbpsSyncStatus?.lastResult?.success ? 'Success' : 'N/A'}
                </p>
              </div>
            </div>
            
            {bbpsSyncStatus?.lastResult && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold mb-2">Last Sync Summary</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{bbpsSyncStatus.lastResult.categories?.fetched || 0}</p>
                    <p className="text-xs text-gray-500">Categories</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{bbpsSyncStatus.lastResult.operators?.fetched || 0}</p>
                    <p className="text-xs text-gray-500">Operators</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{bbpsSyncStatus.lastResult.plans?.fetched || 0}</p>
                    <p className="text-xs text-gray-500">Plans</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-600">{bbpsSyncStatus.lastResult.durationMs || 0}ms</p>
                    <p className="text-xs text-gray-500">Duration</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Configuration Display */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">Configuration Settings</h3>
              <button 
                onClick={async () => {
                  try {
                    await api.clearBBPSCache();
                    loadTabData('bbps');
                    setSuccessMsg('BBPS cache cleared');
                  } catch (err: any) {
                    setError(err.message);
                  }
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border rounded"
              >
                Clear Cache
              </button>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium mb-2">Catalog Sync Settings</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Enabled:</span> <span className={bbpsConfig?.catalogSync?.enabled ? 'text-green-600' : 'text-gray-500'}>{bbpsConfig?.catalogSync?.enabled ? 'Yes' : 'No'}</span></p>
                  <p><span className="text-gray-500">Interval:</span> {bbpsConfig?.catalogSync?.intervalHours || 24} hours</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Health Check Settings</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-gray-500">Enabled:</span> <span className={bbpsConfig?.healthCheck?.enabled ? 'text-green-600' : 'text-gray-500'}>{bbpsConfig?.healthCheck?.enabled ? 'Yes' : 'No'}</span></p>
                  <p><span className="text-gray-500">Interval:</span> {bbpsConfig?.healthCheck?.intervalMinutes || 5} minutes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== AUDIT TAB ========== */}
      {!loading && activeTab === 'audit' && (
        <div className="space-y-6">
          {auditActions && (
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="font-semibold mb-4">This Week's Activity</h3>
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{auditActions.earnTransactions || 0}</p>
                  <p className="text-xs">Earn Txns</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xl font-bold text-blue-600">{auditActions.redeemTransactions || 0}</p>
                  <p className="text-xs">Redeem Txns</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xl font-bold text-purple-600">{auditActions.newUsers || 0}</p>
                  <p className="text-xs">New Users</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-600">{formatNumber(auditActions.earnPoints)}</p>
                  <p className="text-xs">Points Earned</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-600">{formatNumber(auditActions.expiredPoints)}</p>
                  <p className="text-xs">Expired</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow overflow-hidden">
            <h3 className="font-semibold p-4 border-b">Recent Audit Logs</h3>
            {auditLogs.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(log.createdAt)}</td>
                      <td className="px-4 py-3 font-medium">{log.action}</td>
                      <td className="px-4 py-3">{log.actor}</td>
                      <td className="px-4 py-3"><StatusBadge status={log.actorType} /></td>
                      <td className="px-4 py-3 text-sm">{log.resourceType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="p-8 text-center text-gray-500">No audit logs found</p>
            )}
          </div>
        </div>
      )}

      {/* API Help Tab */}
      {activeTab === 'api-help' && (
        <div className="h-[calc(100vh-140px)] -m-6">
          <PointsApiHelpPage />
        </div>
      )}

      {/* ========== CONFIGURATION EDIT MODALS ========== */}

      {/* Edit Earning Tiers Modal */}
      {editModal === 'earning-tiers' && editData && (
        <Modal title="Edit Earning Tiers" onClose={() => setEditModal(null)}>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {editData.tiers?.map((tier: any, idx: number) => (
              <div key={tier.id} className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p className="font-medium text-sm">{tier.name}</p>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">Points/₹1000</label>
                    <input type="number" value={tier.pointsPerThousand} onChange={(e) => {
                      const newTiers = [...editData.tiers];
                      newTiers[idx].pointsPerThousand = parseInt(e.target.value) || 0;
                      setEditData({...editData, tiers: newTiers});
                    }} className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Min (₹)</label>
                    <input type="number" value={tier.minAmount} onChange={(e) => {
                      const newTiers = [...editData.tiers];
                      newTiers[idx].minAmount = parseInt(e.target.value) || 0;
                      setEditData({...editData, tiers: newTiers});
                    }} className="w-full px-2 py-1 border rounded text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">Max (₹)</label>
                    <input type="number" value={tier.maxAmount || ''} onChange={(e) => {
                      const newTiers = [...editData.tiers];
                      newTiers[idx].maxAmount = e.target.value ? parseInt(e.target.value) : null;
                      setEditData({...editData, tiers: newTiers});
                    }} className="w-full px-2 py-1 border rounded text-sm" placeholder="∞" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={tier.isActive} onChange={(e) => {
                    const newTiers = [...editData.tiers];
                    newTiers[idx].isActive = e.target.checked;
                    setEditData({...editData, tiers: newTiers});
                  }} />
                  Active
                </label>
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('earning', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Welcome Bonus Modal */}
      {editModal === 'welcome-bonus' && editData && (
        <Modal title="Edit Welcome Bonus" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.welcomeBonus?.enabled} onChange={(e) => 
                setEditData({...editData, welcomeBonus: {...editData.welcomeBonus, enabled: e.target.checked}})
              } />
              <span className="font-medium">Enable Welcome Bonus</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Bonus Points</label>
              <input type="number" value={editData.welcomeBonus?.points} onChange={(e) => 
                setEditData({...editData, welcomeBonus: {...editData.welcomeBonus, points: parseInt(e.target.value) || 0}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Validity (Days)</label>
              <input type="number" value={editData.welcomeBonus?.validityDays} onChange={(e) => 
                setEditData({...editData, welcomeBonus: {...editData.welcomeBonus, validityDays: parseInt(e.target.value) || 90}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Min Invoice Amount (₹)</label>
              <input type="number" value={editData.welcomeBonus?.minInvoiceAmount} onChange={(e) => 
                setEditData({...editData, welcomeBonus: {...editData.welcomeBonus, minInvoiceAmount: parseInt(e.target.value) || 0}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('earning', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Bonus Multiplier Modal */}
      {editModal === 'bonus-multiplier' && editData && (
        <Modal title="Edit Bonus Multiplier" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.bonusMultiplier?.enabled} onChange={(e) => 
                setEditData({...editData, bonusMultiplier: {...editData.bonusMultiplier, enabled: e.target.checked}})
              } />
              <span className="font-medium">Enable Bonus Multiplier</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Multiplier</label>
              <input type="number" step="0.5" min="1" value={editData.bonusMultiplier?.multiplier} onChange={(e) => 
                setEditData({...editData, bonusMultiplier: {...editData.bonusMultiplier, multiplier: parseFloat(e.target.value) || 1}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Start Date</label>
              <input type="datetime-local" value={editData.bonusMultiplier?.startDate?.slice(0,16) || ''} onChange={(e) => 
                setEditData({...editData, bonusMultiplier: {...editData.bonusMultiplier, startDate: e.target.value ? new Date(e.target.value).toISOString() : null}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date</label>
              <input type="datetime-local" value={editData.bonusMultiplier?.endDate?.slice(0,16) || ''} onChange={(e) => 
                setEditData({...editData, bonusMultiplier: {...editData.bonusMultiplier, endDate: e.target.value ? new Date(e.target.value).toISOString() : null}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('earning', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Earning Limits Modal */}
      {editModal === 'earning-limits' && editData && (
        <Modal title="Edit Earning Limits" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Max Points per Invoice</label>
              <input type="number" value={editData.limits?.maxPointsPerInvoice} onChange={(e) => 
                setEditData({...editData, limits: {...editData.limits, maxPointsPerInvoice: parseInt(e.target.value) || 0}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Points per Day</label>
              <input type="number" value={editData.limits?.maxPointsPerDay} onChange={(e) => 
                setEditData({...editData, limits: {...editData.limits, maxPointsPerDay: parseInt(e.target.value) || 0}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Points per Month</label>
              <input type="number" value={editData.limits?.maxPointsPerMonth} onChange={(e) => 
                setEditData({...editData, limits: {...editData.limits, maxPointsPerMonth: parseInt(e.target.value) || 0}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('earning', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Expiry Rules Modal */}
      {editModal === 'expiry-rules' && editData && (
        <Modal title="Edit Expiry Rules" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.enabled} onChange={(e) => setEditData({...editData, enabled: e.target.checked})} />
              <span className="font-medium">Enable Expiry Processing</span>
            </label>
            <div className="space-y-3">
              {Object.entries(editData.rules || {}).map(([type, rule]: [string, any]) => (
                <div key={type} className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-sm capitalize mb-2">{type.replace('_', ' ')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Expiry Days</label>
                      <input type="number" value={rule.expiryDays} onChange={(e) => {
                        const newRules = {...editData.rules};
                        newRules[type].expiryDays = parseInt(e.target.value) || 0;
                        setEditData({...editData, rules: newRules});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Grace Period</label>
                      <input type="number" value={rule.gracePeriodDays} onChange={(e) => {
                        const newRules = {...editData.rules};
                        newRules[type].gracePeriodDays = parseInt(e.target.value) || 0;
                        setEditData({...editData, rules: newRules});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('expiry', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Redemption Modal */}
      {editModal === 'redemption' && editData && (
        <Modal title="Edit Redemption Settings" onClose={() => setEditModal(null)} wide>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-sm mb-2">Conversion Rate</p>
              <div>
                <label className="text-xs text-gray-500">Points per Rupee</label>
                <input type="number" value={editData.conversionRate?.pointsPerRupee} onChange={(e) => 
                  setEditData({...editData, conversionRate: {...editData.conversionRate, pointsPerRupee: parseInt(e.target.value) || 100}})
                } className="w-full px-2 py-1 border rounded text-sm" />
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-sm mb-2">Minimums</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Min Points</label>
                  <input type="number" value={editData.minimums?.minPointsToRedeem} onChange={(e) => 
                    setEditData({...editData, minimums: {...editData.minimums, minPointsToRedeem: parseInt(e.target.value) || 0}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Min Amount (₹)</label>
                  <input type="number" value={editData.minimums?.minTransactionAmount} onChange={(e) => 
                    setEditData({...editData, minimums: {...editData.minimums, minTransactionAmount: parseInt(e.target.value) || 0}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
              </div>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-sm mb-2">Default Split Strategy</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Max Points %</label>
                  <input type="number" max="100" value={editData.splitStrategy?.default?.maxPointsPercentage} onChange={(e) => 
                    setEditData({...editData, splitStrategy: {...editData.splitStrategy, default: {...editData.splitStrategy.default, maxPointsPercentage: parseInt(e.target.value) || 0}}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Min Cash %</label>
                  <input type="number" max="100" value={editData.splitStrategy?.default?.minCashPercentage} onChange={(e) => 
                    setEditData({...editData, splitStrategy: {...editData.splitStrategy, default: {...editData.splitStrategy.default, minCashPercentage: parseInt(e.target.value) || 0}}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
              </div>
            </div>
            
            {/* Redemption Categories */}
            <div className="border-t pt-4">
              <p className="font-medium text-sm mb-3">Redemption Categories</p>
              {editData.categories?.map((cat: any, idx: number) => (
                <div key={cat.id} className="p-3 bg-gray-50 rounded-lg mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm">{cat.name}</span>
                    <label className="flex items-center gap-1 text-xs">
                      <input type="checkbox" checked={cat.isActive} onChange={(e) => {
                        const newCats = [...editData.categories];
                        newCats[idx].isActive = e.target.checked;
                        setEditData({...editData, categories: newCats});
                      }} />
                      Active
                    </label>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="text-xs text-gray-500">Max Pts %</label>
                      <input type="number" max="100" value={cat.maxPointsPercentage} onChange={(e) => {
                        const newCats = [...editData.categories];
                        newCats[idx].maxPointsPercentage = parseInt(e.target.value) || 0;
                        setEditData({...editData, categories: newCats});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Min Cash %</label>
                      <input type="number" max="100" value={cat.minCashPercentage} onChange={(e) => {
                        const newCats = [...editData.categories];
                        newCats[idx].minCashPercentage = parseInt(e.target.value) || 0;
                        setEditData({...editData, categories: newCats});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Min ₹</label>
                      <input type="number" value={cat.minAmount} onChange={(e) => {
                        const newCats = [...editData.categories];
                        newCats[idx].minAmount = parseInt(e.target.value) || 0;
                        setEditData({...editData, categories: newCats});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Max ₹</label>
                      <input type="number" value={cat.maxAmount} onChange={(e) => {
                        const newCats = [...editData.categories];
                        newCats[idx].maxAmount = parseInt(e.target.value) || 0;
                        setEditData({...editData, categories: newCats});
                      }} className="w-full px-2 py-1 border rounded text-sm" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('redemption', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Notifications Modal */}
      {editModal === 'notifications' && editData && (
        <Modal title="Edit Notification Settings" onClose={() => setEditModal(null)}>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.enabled} onChange={(e) => setEditData({...editData, enabled: e.target.checked})} />
              <span className="font-medium">Enable Notifications</span>
            </label>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-sm mb-2">Quiet Hours</p>
              <label className="flex items-center gap-2 mb-2">
                <input type="checkbox" checked={editData.quietHours?.enabled} onChange={(e) => 
                  setEditData({...editData, quietHours: {...editData.quietHours, enabled: e.target.checked}})
                } />
                <span className="text-sm">Enable Quiet Hours</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Start Time</label>
                  <input type="time" value={editData.quietHours?.startTime} onChange={(e) => 
                    setEditData({...editData, quietHours: {...editData.quietHours, startTime: e.target.value}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">End Time</label>
                  <input type="time" value={editData.quietHours?.endTime} onChange={(e) => 
                    setEditData({...editData, quietHours: {...editData.quietHours, endTime: e.target.value}})
                  } className="w-full px-2 py-1 border rounded text-sm" />
                </div>
              </div>
            </div>
            <div>
              <p className="font-medium text-sm mb-2">Events</p>
              {editData.events?.map((event: any, idx: number) => (
                <label key={event.id} className="flex items-center gap-2 py-1">
                  <input type="checkbox" checked={event.enabled} onChange={(e) => {
                    const newEvents = [...editData.events];
                    newEvents[idx].enabled = e.target.checked;
                    setEditData({...editData, events: newEvents});
                  }} />
                  <span className="text-sm">{event.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('notifications', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Payment Modal */}
      {editModal === 'payment' && editData && (
        <Modal title="Edit Payment Gateway Settings" onClose={() => setEditModal(null)}>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Active Gateway Selection */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium mb-2">Active Payment Gateway</label>
              <select value={editData.activeGateway || 'none'} onChange={(e) => setEditData({...editData, activeGateway: e.target.value})} className="w-full px-4 py-2 border rounded-lg bg-white">
                <option value="none">None (Simulated - for testing)</option>
                <option value="razorpay">Razorpay</option>
                <option value="payu">PayU</option>
              </select>
              <p className="text-xs text-blue-600 mt-2">This gateway will be used for processing Mixed Redemption cash payments</p>
            </div>

            {/* Transaction Limits */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="font-medium text-sm mb-3">Transaction Limits</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Max Amount (₹)</label>
                  <input type="number" value={editData.transactionLimits?.maxAmount} onChange={(e) => 
                    setEditData({...editData, transactionLimits: {...editData.transactionLimits, maxAmount: parseInt(e.target.value) || 0}})
                  } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="100000" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Max Per Day</label>
                  <input type="number" value={editData.transactionLimits?.maxPerDay} onChange={(e) => 
                    setEditData({...editData, transactionLimits: {...editData.transactionLimits, maxPerDay: parseInt(e.target.value) || 0}})
                  } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="10" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Cooldown (sec)</label>
                  <input type="number" value={editData.transactionLimits?.cooldownSeconds} onChange={(e) => 
                    setEditData({...editData, transactionLimits: {...editData.transactionLimits, cooldownSeconds: parseInt(e.target.value) || 0}})
                  } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="60" />
                </div>
              </div>
            </div>

            {/* Razorpay Configuration */}
            <div className={`p-4 rounded-lg border ${editData.razorpay?.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">💳</span>
                  <span className="font-medium">Razorpay</span>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editData.razorpay?.enabled} onChange={(e) => 
                    setEditData({...editData, razorpay: {...editData.razorpay, enabled: e.target.checked}})
                  } className="w-4 h-4" />
                  <span className="text-sm">Enabled</span>
                </label>
              </div>
              
              {editData.razorpay?.enabled && (
                <div className="space-y-3 pt-2 border-t border-green-200">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Environment</label>
                    <select value={editData.razorpay?.mode} onChange={(e) => 
                      setEditData({...editData, razorpay: {...editData.razorpay, mode: e.target.value}})
                    } className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
                      <option value="test">Test Mode (Sandbox)</option>
                      <option value="live">Live Mode (Production)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Key ID *</label>
                      <input type="text" value={editData.razorpay?.keyId || ''} onChange={(e) => 
                        setEditData({...editData, razorpay: {...editData.razorpay, keyId: e.target.value}})
                      } className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="rzp_test_xxxxx" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Key Secret *</label>
                      <input type="password" value={editData.razorpay?.keySecret || ''} onChange={(e) => 
                        setEditData({...editData, razorpay: {...editData.razorpay, keySecret: e.target.value}})
                      } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Leave empty to keep existing" />
                    </div>
                  </div>
                  <div className="pt-2 border-t border-green-100">
                    <label className="flex items-center gap-2 mb-2">
                      <input type="checkbox" checked={editData.razorpay?.webhookEnabled} onChange={(e) => 
                        setEditData({...editData, razorpay: {...editData.razorpay, webhookEnabled: e.target.checked}})
                      } />
                      <span className="text-sm font-medium">Enable Webhooks</span>
                    </label>
                    {editData.razorpay?.webhookEnabled && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Webhook Secret</label>
                          <input type="password" value={editData.razorpay?.webhookSecret || ''} onChange={(e) => 
                            setEditData({...editData, razorpay: {...editData.razorpay, webhookSecret: e.target.value}})
                          } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Leave empty to keep existing" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600 block mb-1">Webhook URL (configure in Razorpay Dashboard)</label>
                          <input type="text" value={editData.razorpay?.webhookUrl || `${window.location.origin}/api/v1/points/payment/webhook/razorpay`} onChange={(e) => 
                            setEditData({...editData, razorpay: {...editData.razorpay, webhookUrl: e.target.value}})
                          } className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="https://your-domain.com/api/v1/points/payment/webhook/razorpay" />
                          <p className="text-xs text-gray-400 mt-1">Copy this URL to Razorpay Dashboard → Webhooks</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="pt-2 border-t border-green-100">
                    <label className="flex items-center gap-2 mb-2">
                      <input type="checkbox" checked={editData.razorpay?.bbps?.enabled} onChange={(e) => 
                        setEditData({...editData, razorpay: {...editData.razorpay, bbps: {...editData.razorpay?.bbps, enabled: e.target.checked}}})
                      } />
                      <span className="text-sm font-medium">Enable Razorpay BBPS</span>
                    </label>
                    {editData.razorpay?.bbps?.enabled && (
                      <div>
                        <label className="text-xs text-gray-600 block mb-1">BBPS Merchant ID</label>
                        <input type="text" value={editData.razorpay?.bbps?.merchantId || ''} onChange={(e) => 
                          setEditData({...editData, razorpay: {...editData.razorpay, bbps: {...editData.razorpay?.bbps, merchantId: e.target.value}}})
                        } className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="Razorpay BBPS Merchant ID" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* PayU Configuration */}
            <div className={`p-4 rounded-lg border ${editData.payu?.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">💳</span>
                  <span className="font-medium">PayU</span>
                </div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editData.payu?.enabled} onChange={(e) => 
                    setEditData({...editData, payu: {...editData.payu, enabled: e.target.checked}})
                  } className="w-4 h-4" />
                  <span className="text-sm">Enabled</span>
                </label>
              </div>
              
              {editData.payu?.enabled && (
                <div className="space-y-3 pt-2 border-t border-green-200">
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Environment</label>
                    <select value={editData.payu?.mode} onChange={(e) => 
                      setEditData({...editData, payu: {...editData.payu, mode: e.target.value}})
                    } className="w-full px-3 py-2 border rounded-lg text-sm bg-white">
                      <option value="test">Test Mode (Sandbox)</option>
                      <option value="live">Live Mode (Production)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Merchant Key *</label>
                      <input type="text" value={editData.payu?.merchantKey || ''} onChange={(e) => 
                        setEditData({...editData, payu: {...editData.payu, merchantKey: e.target.value}})
                      } className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="PayU Merchant Key" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600 block mb-1">Merchant Salt *</label>
                      <input type="password" value={editData.payu?.merchantSalt || ''} onChange={(e) => 
                        setEditData({...editData, payu: {...editData.payu, merchantSalt: e.target.value}})
                      } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Leave empty to keep existing" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-600 block mb-1">Webhook URL (configure in PayU Dashboard)</label>
                    <input type="text" value={editData.payu?.webhookUrl || `${window.location.origin}/api/v1/points/payment/webhook/payu`} onChange={(e) => 
                      setEditData({...editData, payu: {...editData.payu, webhookUrl: e.target.value}})
                    } className="w-full px-3 py-2 border rounded-lg text-sm font-mono" placeholder="https://your-domain.com/api/v1/points/payment/webhook/payu" />
                    <p className="text-xs text-gray-400 mt-1">Copy this URL to PayU Dashboard → Webhooks</p>
                  </div>
                </div>
              )}
            </div>

            {/* Retry Configuration */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="font-medium text-sm mb-3">Retry Configuration</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Max Attempts</label>
                  <input type="number" value={editData.retry?.maxAttempts || 3} onChange={(e) => 
                    setEditData({...editData, retry: {...editData.retry, maxAttempts: parseInt(e.target.value) || 3}})
                  } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="3" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Backoff (ms, comma-separated)</label>
                  <input type="text" value={(editData.retry?.backoffMs || [1000, 2000, 4000]).join(', ')} onChange={(e) => 
                    setEditData({...editData, retry: {...editData.retry, backoffMs: e.target.value.split(',').map(s => parseInt(s.trim()) || 1000)}})
                  } className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="1000, 2000, 4000" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-4 mt-4 pt-4 border-t">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={() => handleSaveConfig('payment_gateway', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </Modal>
      )}

      {/* Edit Migration Config Modal */}
      {editModal === 'migration' && editData && (
        <Modal title="Edit Migration Configuration" onClose={() => setEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.settings?.enabled} onChange={(e) => 
                setEditData({...editData, settings: {...editData.settings, enabled: e.target.checked}})
              } />
              <span className="font-medium">Enable Migration</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Conversion Rate (₹1 = X points)</label>
              <input type="number" value={editData.settings?.conversionRate} onChange={(e) => 
                setEditData({...editData, settings: {...editData.settings, conversionRate: parseInt(e.target.value) || 100}})
              } className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Mode</label>
              <select value={editData.settings?.mode} onChange={(e) => 
                setEditData({...editData, settings: {...editData.settings, mode: e.target.value}})
              } className="w-full px-4 py-2 border rounded-lg">
                <option value="controlled">Controlled (Manual batches)</option>
                <option value="automatic">Automatic</option>
              </select>
            </div>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={editData.rollback?.enabled} onChange={(e) => 
                setEditData({...editData, rollback: {...editData.rollback, enabled: e.target.checked}})
              } />
              <span className="font-medium">Enable Rollback</span>
            </label>
            {editData.rollback?.enabled && (
              <div>
                <label className="block text-sm font-medium mb-1">Rollback Window (Days)</label>
                <input type="number" value={editData.rollback?.windowDays} onChange={(e) => 
                  setEditData({...editData, rollback: {...editData.rollback, windowDays: parseInt(e.target.value) || 7}})
                } className="w-full px-4 py-2 border rounded-lg" />
              </div>
            )}
          </div>
          <div className="flex gap-4 mt-4">
            <button onClick={() => setEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button>
            <button onClick={() => handleSaveConfig('migration', editData)} disabled={saving} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </Modal>
      )}

      {/* Create New Migration Batch Modal */}
      {showNewBatchModal && (
        <Modal title="Create New Migration Batch" onClose={() => { setShowNewBatchModal(false); setPendingMigrationUsers([]); }}>
          <div className="space-y-4">
            {pendingMigrationUsers.length > 0 ? (
              <div className="p-3 bg-green-50 rounded-lg text-sm text-green-800">
                <p className="font-medium mb-2">✓ {pendingMigrationUsers.length} user(s) selected from SQL Server:</p>
                <div className="max-h-32 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-green-200">
                        <th className="text-left py-1">User ID</th>
                        <th className="text-left py-1">Name</th>
                        <th className="text-right py-1">Legacy Points (₹)</th>
                        <th className="text-right py-1">→ New Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingMigrationUsers.map(u => (
                        <tr key={u.userId} className="border-b border-green-100">
                          <td className="py-1 font-mono">{u.userId}</td>
                          <td className="py-1">{u.name}</td>
                          <td className="py-1 text-right">₹{formatNumber(u.legacyPoints)}</td>
                          <td className="py-1 text-right font-semibold">{formatNumber(u.legacyPoints * (migrationConfig?.settings?.conversionRate || 100))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-xs">Total: ₹{formatNumber(pendingMigrationUsers.reduce((s, u) => s + u.legacyPoints, 0))} → {formatNumber(pendingMigrationUsers.reduce((s, u) => s + u.legacyPoints, 0) * (migrationConfig?.settings?.conversionRate || 100))} points</p>
              </div>
            ) : (
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">How Migration Works:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Create a batch with user IDs to migrate</li>
                  <li>Execute the batch to convert legacy points</li>
                  <li>Points are converted using the configured rate (₹1 = {migrationConfig?.settings?.conversionRate || 100} points)</li>
                </ol>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Batch Name *</label>
              <input type="text" value={newBatchName} onChange={(e) => setNewBatchName(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="e.g., January 2026 Migration" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input type="text" value={newBatchDescription} onChange={(e) => setNewBatchDescription(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Optional description" />
            </div>
            {pendingMigrationUsers.length === 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">User IDs to Migrate</label>
                <textarea 
                  value={newBatchUserIds} 
                  onChange={(e) => setNewBatchUserIds(e.target.value)} 
                  className="w-full px-4 py-2 border rounded-lg font-mono text-sm" 
                  rows={4} 
                  placeholder="Enter user IDs (comma or newline separated)&#10;e.g., user_001, user_002&#10;or one per line"
                />
                <p className="text-xs text-gray-500 mt-1">Tip: Select users from SQL Users tab to auto-populate with legacy points</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={() => { setShowNewBatchModal(false); setPendingMigrationUsers([]); }} className="px-4 py-2 border rounded-lg">Cancel</button>
            {pendingMigrationUsers.length > 0 ? (
              <button 
                onClick={handleCreateAndExecuteBatch} 
                disabled={saving || !newBatchName.trim()} 
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg disabled:opacity-50 hover:bg-green-700"
              >
                {saving ? 'Migrating...' : `Create & Execute Migration (${pendingMigrationUsers.length} users)`}
              </button>
            ) : (
              <button onClick={handleCreateBatch} disabled={saving || !newBatchName.trim()} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg disabled:opacity-50">
                {saving ? 'Creating...' : 'Create Batch'}
              </button>
            )}
          </div>
        </Modal>
      )}

      {/* Batch Details Modal */}
      {selectedBatch && (
        <Modal title="Migration Batch Details" onClose={() => setSelectedBatch(null)} wide>
          <div className="space-y-4">
            {/* Header */}
            <div className="flex justify-between items-start">
              <div>
                <h4 className="text-lg font-bold">{selectedBatch.name}</h4>
                {selectedBatch.description && <p className="text-gray-500 text-sm">{selectedBatch.description}</p>}
              </div>
              <StatusBadge status={selectedBatch.status} />
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-3">
              <div className="p-3 bg-blue-50 rounded-lg text-center">
                <p className="text-xl font-bold text-blue-600">{selectedBatch.stats?.totalUsers || 0}</p>
                <p className="text-xs text-gray-600">Total Users</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center">
                <p className="text-xl font-bold text-green-600">{selectedBatch.stats?.processedUsers || 0}</p>
                <p className="text-xs text-gray-600">Processed</p>
              </div>
              <div className="p-3 bg-red-50 rounded-lg text-center">
                <p className="text-xl font-bold text-red-600">{selectedBatch.stats?.failedUsers || 0}</p>
                <p className="text-xs text-gray-600">Failed</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg text-center">
                <p className="text-xl font-bold text-orange-600">{formatNumber(selectedBatch.stats?.totalPointsMigrated || 0)}</p>
                <p className="text-xs text-gray-600">Points Migrated</p>
              </div>
            </div>

            {/* Details */}
            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Batch ID:</span> <span className="font-mono text-xs">{selectedBatch.id}</span></div>
                <div><span className="text-gray-500">Status:</span> <span className="font-medium capitalize">{selectedBatch.status}</span></div>
                <div><span className="text-gray-500">Created By:</span> <span>{selectedBatch.createdBy || '-'}</span></div>
                <div><span className="text-gray-500">Executed By:</span> <span>{selectedBatch.executedBy || '-'}</span></div>
                <div><span className="text-gray-500">Created At:</span> <span>{formatDate(selectedBatch.createdAt)}</span></div>
                <div><span className="text-gray-500">Executed At:</span> <span>{formatDate(selectedBatch.executedAt)}</span></div>
              </div>
            </div>

            {/* Conversion Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm"><span className="text-gray-600">Conversion Rate Applied:</span> <span className="font-bold">₹1 = {selectedBatch.conversionRate || migrationConfig?.settings?.conversionRate || 100} points</span></p>
              {selectedBatch.stats?.totalLegacyAmount && (
                <p className="text-sm mt-1"><span className="text-gray-600">Legacy Amount Converted:</span> <span className="font-bold">₹{formatNumber(selectedBatch.stats.totalLegacyAmount)}</span></p>
              )}
            </div>

            {/* Migrated Users Details - NEW SECTION */}
            {batchDetailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-linepe-orange"></div>
                <span className="ml-3 text-gray-500">Loading user details...</span>
              </div>
            ) : selectedBatch.migratedUsers && selectedBatch.migratedUsers.length > 0 ? (
              <div>
                <p className="text-sm font-medium mb-2">Migrated Users ({selectedBatch.migratedUsers.length})</p>
                <div className="bg-white border rounded-lg max-h-64 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">User ID</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Legacy Amount</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Points Migrated</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rate</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Migrated At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {selectedBatch.migratedUsers.map((user: any) => (
                        <tr key={user.userId} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono text-xs">{user.userId}</td>
                          <td className="px-3 py-2">₹{formatNumber(user.legacyAmount)}</td>
                          <td className="px-3 py-2 font-bold text-green-600">{formatNumber(user.pointsMigrated)}</td>
                          <td className="px-3 py-2 text-gray-500">₹1={user.conversionRate}</td>
                          <td className="px-3 py-2 text-gray-500 text-xs">{formatDate(user.migratedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : selectedBatch.status === 'pending' ? (
              <div className="bg-yellow-50 p-4 rounded-lg text-center text-sm text-yellow-800">
                This batch is pending execution. Execute the batch to migrate users.
              </div>
            ) : selectedBatch.transactionCount === 0 ? (
              <div className="bg-gray-50 p-4 rounded-lg text-center text-sm text-gray-500">
                No migration transactions found for this batch.
              </div>
            ) : null}

            {/* Error Details if failed */}
            {selectedBatch.status === 'failed' && (selectedBatch.error || selectedBatch.errorMessage) && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-sm font-medium text-red-800 mb-1">Error Details</p>
                <p className="text-sm text-red-700 font-mono">{selectedBatch.error || selectedBatch.errorMessage}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2 border-t">
              {selectedBatch.status === 'pending' && (
                <button onClick={() => { handleExecuteBatch(selectedBatch.id); setSelectedBatch(null); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Execute This Batch
                </button>
              )}
              {selectedBatch.status === 'completed' && (
                <button onClick={() => { handleRollbackBatch(selectedBatch.id); setSelectedBatch(null); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Rollback This Batch
                </button>
              )}
              <button onClick={() => setSelectedBatch(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50 ml-auto">
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* BBPS PayU Configuration Modal */}
      {bbpsEditModal === 'payu' && bbpsEditData && (
        <Modal title="Configure PayU BBPS" onClose={() => setBbpsEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={bbpsEditData.enabled} onChange={(e) => 
                setBbpsEditData({...bbpsEditData, enabled: e.target.checked})
              } />
              <span className="font-medium">Enable PayU BBPS</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Environment</label>
              <select value={bbpsEditData.environment} onChange={(e) => 
                setBbpsEditData({...bbpsEditData, environment: e.target.value})
              } className="w-full px-4 py-2 border rounded-lg">
                <option value="test">Test (Sandbox)</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Credentials</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Client ID</label>
                  <input type="text" value={bbpsEditData.clientId} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, clientId: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="PayU Client ID" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Client Secret (or Key Vault Reference)</label>
                  <input type="password" value={bbpsEditData.clientSecretRef} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, clientSecretRef: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="Leave empty to keep existing" />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to keep the existing secret</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Agent ID</label>
                  <input type="text" value={bbpsEditData.agentId} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, agentId: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="PayU Agent ID" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Grant Type</label>
                  <input type="text" value={bbpsEditData.grantType} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, grantType: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="client_credentials" />
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Endpoints</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Auth Host</label>
                  <input type="text" value={bbpsEditData.authHost} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, authHost: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="https://uat-accounts.payu.in" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">NBC Host</label>
                  <input type="text" value={bbpsEditData.nbcHost} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, nbcHost: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="https://bbps-sb.payu.in" />
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Webhook / Callback</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Callback URL (for PayU to send transaction status updates)</label>
                  <input type="text" value={bbpsEditData.callbackUrl || `${window.location.origin}/api/v1/points/bbps/webhook/payu`} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, callbackUrl: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg font-mono text-sm" placeholder="https://your-domain.com/api/v1/points/bbps/webhook/payu" />
                  <p className="text-xs text-gray-400 mt-1">Configure this URL in PayU Dashboard for transaction status callbacks</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Webhook Secret (for signature verification)</label>
                  <input type="password" value={bbpsEditData.webhookSecretRef || ''} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, webhookSecretRef: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="Leave empty to keep existing" />
                </div>
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <button onClick={() => setBbpsEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button 
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.request('/api/admin/points/bbps/config/payu', {
                      method: 'PUT',
                      body: JSON.stringify({
                        enabled: bbpsEditData.enabled,
                        environment: bbpsEditData.environment,
                        credentials: {
                          clientId: bbpsEditData.clientId,
                          ...(bbpsEditData.clientSecretRef ? { clientSecretRef: bbpsEditData.clientSecretRef } : {}),
                          agentId: bbpsEditData.agentId,
                          grantType: bbpsEditData.grantType,
                        },
                        endpoints: {
                          authHost: bbpsEditData.authHost,
                          nbcHost: bbpsEditData.nbcHost,
                        },
                        webhook: {
                          callbackUrl: bbpsEditData.callbackUrl || `${window.location.origin}/api/v1/points/bbps/webhook/payu`,
                          ...(bbpsEditData.webhookSecretRef ? { webhookSecretRef: bbpsEditData.webhookSecretRef } : {}),
                        },
                      }),
                    });
                    setSuccessMsg('PayU configuration saved');
                    setBbpsEditModal(null);
                    loadTabData('bbps');
                  } catch (err: any) {
                    setError(err.message);
                  } finally {
                    setSaving(false);
                  }
                }} 
                disabled={saving} 
                className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* BBPS Razorpay Configuration Modal */}
      {bbpsEditModal === 'razorpay' && bbpsEditData && (
        <Modal title="Configure Razorpay BBPS" onClose={() => setBbpsEditModal(null)}>
          <div className="space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={bbpsEditData.enabled} onChange={(e) => 
                setBbpsEditData({...bbpsEditData, enabled: e.target.checked})
              } />
              <span className="font-medium">Enable Razorpay BBPS</span>
            </label>
            <div>
              <label className="block text-sm font-medium mb-1">Environment</label>
              <select value={bbpsEditData.environment} onChange={(e) => 
                setBbpsEditData({...bbpsEditData, environment: e.target.value})
              } className="w-full px-4 py-2 border rounded-lg">
                <option value="test">Test (Sandbox)</option>
                <option value="production">Production</option>
              </select>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Credentials</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Key ID</label>
                  <input type="text" value={bbpsEditData.keyId} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, keyId: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="Razorpay Key ID" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Key Secret (or Key Vault Reference)</label>
                  <input type="password" value={bbpsEditData.keySecretRef} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, keySecretRef: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="Leave empty to keep existing" />
                  <p className="text-xs text-gray-400 mt-1">Leave empty to keep the existing secret</p>
                </div>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Endpoints</p>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Base URL</label>
                <input type="text" value={bbpsEditData.baseUrl} onChange={(e) => 
                  setBbpsEditData({...bbpsEditData, baseUrl: e.target.value})
                } className="w-full px-4 py-2 border rounded-lg" placeholder="https://api.razorpay.com" />
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Webhook / Callback</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Webhook URL (for Razorpay to send transaction status updates)</label>
                  <input type="text" value={bbpsEditData.webhookUrl || `${window.location.origin}/api/v1/points/bbps/webhook/razorpay`} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, webhookUrl: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg font-mono text-sm" placeholder="https://your-domain.com/api/v1/points/bbps/webhook/razorpay" />
                  <p className="text-xs text-gray-400 mt-1">Configure this URL in Razorpay Dashboard for transaction status webhooks</p>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Webhook Secret (for signature verification)</label>
                  <input type="password" value={bbpsEditData.webhookSecretRef || ''} onChange={(e) => 
                    setBbpsEditData({...bbpsEditData, webhookSecretRef: e.target.value})
                  } className="w-full px-4 py-2 border rounded-lg" placeholder="Leave empty to keep existing" />
                </div>
              </div>
            </div>
            <div className="flex gap-4 pt-4">
              <button onClick={() => setBbpsEditModal(null)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button 
                onClick={async () => {
                  setSaving(true);
                  try {
                    await api.request('/api/admin/points/bbps/config/razorpay', {
                      method: 'PUT',
                      body: JSON.stringify({
                        enabled: bbpsEditData.enabled,
                        environment: bbpsEditData.environment,
                        credentials: {
                          keyId: bbpsEditData.keyId,
                          ...(bbpsEditData.keySecretRef ? { keySecretRef: bbpsEditData.keySecretRef } : {}),
                        },
                        endpoints: {
                          baseUrl: bbpsEditData.baseUrl,
                        },
                        webhook: {
                          webhookUrl: bbpsEditData.webhookUrl || `${window.location.origin}/api/v1/points/bbps/webhook/razorpay`,
                          ...(bbpsEditData.webhookSecretRef ? { webhookSecretRef: bbpsEditData.webhookSecretRef } : {}),
                        },
                      }),
                    });
                    setSuccessMsg('Razorpay configuration saved');
                    setBbpsEditModal(null);
                    loadTabData('bbps');
                  } catch (err: any) {
                    setError(err.message);
                  } finally {
                    setSaving(false);
                  }
                }} 
                disabled={saving} 
                className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Adjust Points Modal */}
      {showAdjustModal && (
        <Modal title="Adjust User Points" onClose={() => setShowAdjustModal(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">User ID</label>
              <input type="text" value={adjustUserId} onChange={(e) => setAdjustUserId(e.target.value)} className="w-full px-4 py-2 border rounded-lg" placeholder="Enter user ID" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select value={adjustType} onChange={(e) => setAdjustType(e.target.value as any)} className="w-full px-4 py-2 border rounded-lg">
                <option value="credit">Credit (Add Points)</option>
                <option value="debit">Debit (Remove Points)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Points</label>
              <input type="number" value={adjustPoints} onChange={(e) => setAdjustPoints(parseInt(e.target.value) || 0)} className="w-full px-4 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Reason</label>
              <textarea value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} className="w-full px-4 py-2 border rounded-lg" rows={2} />
            </div>
            <div className="flex gap-4">
              <button onClick={() => setShowAdjustModal(false)} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleAdjustPoints} disabled={!adjustUserId || !adjustPoints || !adjustReason} className="flex-1 px-4 py-2 bg-linepe-orange text-white rounded-lg hover:bg-orange-600 disabled:opacity-50">
                {adjustType === 'credit' ? 'Add Points' : 'Remove Points'}
              </button>
            </div>
          </div>
        </Modal>
      )}
        </div>
      </div>
    </div>
  );
}

// Helper Components
function StatCard({ title, value, icon, subtitle, color }: { title: string; value: number; icon: string; subtitle?: string; color: string }) {
  const colors: Record<string, string> = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600',
  };
  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-600 text-sm">{title}</p>
          <p className={`text-2xl font-bold ${colors[color]?.split(' ')[1] || 'text-gray-900'}`}>
            {new Intl.NumberFormat('en-IN').format(value || 0)}
          </p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`text-3xl p-2 rounded-lg ${colors[color]}`}>{icon}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    completed: 'bg-green-100 text-green-800',
    processing: 'bg-blue-100 text-blue-800',
    pending: 'bg-yellow-100 text-yellow-800',
    failed: 'bg-red-100 text-red-800',
    admin: 'bg-purple-100 text-purple-800',
    system: 'bg-blue-100 text-blue-800',
    rolled_back: 'bg-gray-100 text-gray-800',
    high: 'bg-red-100 text-red-800',
    normal: 'bg-blue-100 text-blue-800',
    low: 'bg-gray-100 text-gray-800',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl ${wide ? 'max-w-2xl' : 'max-w-lg'} w-full mx-4 max-h-[85vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold">{title}</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">×</button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
